/**
 * editor.ts — надписи КОНСТРУКТОРА для автора (WEB-1874, этап 3, порция E3).
 *
 * ЧТО СЮДА ВХОДИТ: оболочка редактора (шапка, панели, тосты), палитра блоков,
 * дерево слоёв, превью, ветвление, гейты публикации, история версий,
 * переключатель языка воронки и правка перевода, выбор цены Stripe, части
 * воронки, режим показа пейволла, галереи тем и стиль-паков.
 *
 * ⚠ ЧЕГО ЗДЕСЬ НЕТ И НЕ ДОЛЖНО БЫТЬ — текста, который видит ПОКУПАТЕЛЬ на
 * публичной странице. У него свой язык (язык ВОРОНКИ) и свой словарь
 * `utils/publicQuizStrings.ts` со сторожем WEB-967. Блоки конструктора
 * рисуются и там, и тут: подпись «Выбор тарифа» в палитре — автору, а надпись
 * на кнопке внутри блока — покупателю. Первое сюда, второе — туда.
 *
 * Панель свойств блока (`BlockPropertiesPanel*`), ИИ-чат и логика ветвления
 * лежат в своих словарях (`editorPanel`, `ai`) — это порции других агентов
 * той же волны.
 */
import { defineDictionary } from '../define';
import { plural } from '../plural';

export const editorStrings = defineDictionary(
  {
    // ── Палитра блоков: подписи типов ───────────────────────────────────────
    // Ключи с префиксом `blockLabel` адресуются из BLOCK_DEFINITIONS по имени,
    // поэтому префикс обязан оставаться свободным от подстановок-функций.
    blockLabelTitle: 'Title',
    blockLabelSubTitle: 'Subtitle',
    blockLabelHeading: 'Section heading',
    blockLabelParagraph: 'Paragraph',
    blockLabelImage: 'Image',
    blockLabelHtml: 'HTML',
    blockLabelSingleChoice: 'Single choice',
    blockLabelMultipleChoice: 'Multiple choice',
    blockLabelLikertScale: 'Emoji scale (Likert)',
    blockLabelMicroFeedback: 'Micro-feedback on answer',
    blockLabelStatementAgreement: 'Statement with reaction (✕/🤔/✓)',
    blockLabelPreferenceCard: 'Preference card (yes/no)',
    blockLabelEmail: 'Email',
    blockLabelTextField: 'Text field',
    blockLabelCheckbox: 'Checkbox',
    blockLabelButtonNext: 'Next button',
    blockLabelButtonLink: 'Link button',
    blockLabelRedirectUrl: 'Redirect to URL',
    blockLabelProgressBar: 'Progress bar',
    blockLabelSpinner: 'Spinner',
    blockLabelResultScreen: 'Result screen',
    blockLabelPriceBlock: 'Price block',
    blockLabelButtonToPay: 'Buy button',
    blockLabelReturnToApp: 'Return to app button',
    blockLabelStripeWalletsExpress: 'Express wallets',
    blockLabelTermsCheckbox: 'Terms checkbox',
    // WEB-2158: явное согласие на обработку ответов о здоровье — стоит на
    // экране квиза рядом с чувствительным вопросом, не на экране оплаты.
    blockLabelSensitiveConsent: 'Explicit consent for sensitive data',
    // WEB-2257: плашка в конструкторе для ниш, где блок согласия НЕ ставится
    // сам (питание, отношения). Имя блока подставляется, чтобы автор нашёл
    // его в списке блоков одним словом, а не догадывался.
    sensitiveConsentNicheHintTitle:
      'This niche often asks about sensitive things',
    sensitiveConsentNicheHintText: (blockLabel: string) =>
      `Diagnoses, allergies, orientation, marital status. If your funnel does ` +
      `ask about any of these, add the “${blockLabel}” block right before the ` +
      `first such question: without explicit consent such answers must not be ` +
      `collected. If it does not — nothing needs to be added.`,
    blockLabelPaymentBanners: 'Payment system banners',
    blockLabelPriceBreakdownText: 'Price breakdown (per day)',
    blockLabelRestorePurchases: 'Restore purchases',
    blockLabelCloseWebview: 'Close window button',
    blockLabelSubscriptionList: 'Portal: subscription list',
    blockLabelCancelButton: 'Portal: cancel button',
    blockLabelResumeButton: 'Portal: resume button',
    blockLabelDownsellOffer: 'Portal: retention offer (discount)',
    // WEB-2090 (остаток WEB-1368 п.3.10): блок НЕ тикает — время считается
    // один раз при отрисовке. Тикающий отсчёт — соседний блок «Countdown».
    blockLabelTimer: 'Static time badge',
    blockLabelDivider: 'Divider',
    blockLabelSpacer: 'Spacer',
    blockLabelIconWithText: 'Icon + text',
    blockLabelBulletList: 'Bulleted list',
    blockLabelNumberedList: 'Numbered list',
    blockLabelAccordion: 'Accordion',
    blockLabelColumns2: '2 columns',
    blockLabelCard: 'Card',
    blockLabelPhoneInput: 'Phone field',
    blockLabelDatePicker: 'Date picker',
    blockLabelNumberInput: 'Number field',
    blockLabelSelectDropdown: 'Dropdown',
    blockLabelRatingStars: 'Star rating',
    blockLabelSliderRange: 'Slider',
    blockLabelToggle: 'Toggle',
    blockLabelNameField: 'Name field',
    blockLabelVideoEmbed: 'Video',
    blockLabelTestimonials: 'Testimonials',
    blockLabelFeatureChips: 'Feature chips',
    blockLabelReviewStars: 'Review rating',
    blockLabelRatingChip: 'Rating chip (social proof)',
    blockLabelTrustBadges: 'Trust badges',
    blockLabelGuaranteeBadge: 'Guarantee badge',
    blockLabelBeforeAfter: 'Before / After',
    blockLabelGallery: 'Gallery',
    blockLabelCountdown: 'Countdown',
    blockLabelStockBadge: 'In-stock badge',
    blockLabelUrgencyBanner: 'Urgency banner',
    blockLabelCallout: 'Callout',
    blockLabelScratchCard: 'Discount scratch card',
    blockLabelBlurTeaser: 'Locked teaser',
    blockLabelForecastChart: 'Forecast chart',
    blockLabelPromoTicket: 'Promo ticket with code',
    blockLabelNowGoalBars: 'Now / goal comparison',
    blockLabelBmiScale: 'BMI scale with slider',
    blockLabelProgressText: 'Progress text',
    blockLabelConditionalBranch: 'Conditional branch',
    blockLabelMultiStepQuestion: 'Multi-step question',
    blockLabelImageChoice: 'Image choice',
    blockLabelWeightPicker: 'Weight picker',
    blockLabelHeightPicker: 'Height picker',
    blockLabelPlanSelector: 'Plan selector',
    blockLabelPaymentTerms: 'Payment terms',
    blockLabelDownsellPopup: 'Downsell popup',
    blockLabelLoaderProcessing: 'Loading / processing',
    blockLabelChart: 'Chart',
    blockLabelCookieConsent: 'Cookie consent',
    blockLabelPhotoUpload: 'Photo upload',
    // WEB-2438 Ж13 (решение владельца 2438-Р2): типы вне палитры — в «Слоях».
    offPaletteLabelHero: 'Cover',
    offPaletteLabelStripeCheckoutForm: 'Pay button (legacy)',

    // ── Палитра блоков: разделы ─────────────────────────────────────────────
    blockCategoryText: 'Text',
    blockCategoryMedia: 'Media',
    blockCategoryQuestions: 'Questions',
    blockCategoryLeads: 'Lead capture',
    blockCategoryNavigation: 'Navigation',
    blockCategoryVisual: 'Visual',
    blockCategoryStructure: 'Structure',
    blockCategoryPayment: 'Payment',
    blockCategoryPortal: 'Subscription portal',
    blockCategoryUrgency: 'Urgency',
    blockCategoryLayout: 'Layout & visual',
    blockCategoryForms: 'Forms',
    blockCategorySocialProof: 'Media & social proof',
    blockCategoryUrgencyConversion: 'Urgency & conversion',
    blockCategoryQuizLogic: 'Quiz logic',

    addBlockTitle: 'Add block',
    blockHiddenOnPostPurchase:
      'This block is not shown on the post-purchase screen',

    // ── Дерево слоёв ────────────────────────────────────────────────────────
    layersTab: 'Layers',
    blocksTab: 'Blocks',
    addScreen: 'Add screen',
    addFirstScreen: 'Add the first screen',
    deleteScreen: 'Delete screen',
    deleteBlock: 'Delete block',
    // WEB-2704: «Дублировать» экран и блок в «Слоях».
    duplicateScreen: 'Duplicate screen',
    duplicateBlock: 'Duplicate block',
    // WEB-4111 (эталон editor-c.jsx:1299, i2.aiBlock): «ИИ» в строке блока «Слоёв».
    layerBlockAi: 'AI',
    screenCopySuffix: ' (copy)',
    screenDuplicated: 'Screen duplicated — the copy is right after the original',
    screenDuplicatedLogicStays: (name: string) =>
      `Screen duplicated. Logic rules stay on “${name}”, the copy has none.`,
    blockDuplicated: 'Block duplicated — the copy is right below',
    noScreensYet: 'No screens yet',
    screenIsOneStep: 'A screen is one step the visitor sees',
    postPurchaseScreen: 'Post-purchase screen',
    postPurchaseNotShown: 'Not shown on the post-purchase screen',
    priceMissingHint: 'No price set — fill it in before publishing',
    imageNotGeneratedHint:
      'The image was not generated — open the block and press “Regenerate”',
    screenImageNotGeneratedHint:
      'An image on this screen was not generated — you can regenerate it on the image block',
    screenHasNoAdvanceLabel: 'This screen has no navigation button',
    noAdvanceHint:
      'This screen has no navigation button. On the published page the visitor ' +
      'gets a fallback “Next” button. That is not an error — you can add your ' +
      'own “Next button” block or leave it as is.',
    // WEB-3767 (screenshots 30.10): one short line instead of a paragraph
    postPurchaseNotChosen: 'No own post-purchase screen — the standard one is shown',
    // WEB-2476 п.2: оговорка о стиле в конструкторе пейволла (WEB-785).
    paywallStyleNoteGeneric:
      'Inside a funnel the pricing screen takes the quiz style if the quiz has one. The style chosen here applies when the quiz has no style or the pricing screen is opened on its own.',
    paywallStyleNoteQuizPack: (style: string) =>
      `In this funnel the quiz style “${style}” applies — the style chosen here applies only when the pricing screen is opened on its own.`,
    // Слово владельца 20.10.2026: панель «Шапка квиза» — свои поля шапки,
    // перебивающие шапку стиля (QuizHeaderFields.tsx).
    quizHeaderSection: 'Quiz header',
    quizHeaderHint:
      'Each field set here overrides the header of the style. “As in the style” brings the style back.',
    quizHeaderAsInStyle: 'As in the style',
    quizHeaderResetAll: 'Whole header as in the style',
    quizHeaderProgress: 'Progress',
    quizHeaderProgressBar: 'Bar',
    quizHeaderProgressSegmented: 'Segments',
    quizHeaderProgressNodes: 'Dots',
    quizHeaderProgressSectionSteps: 'Section steps',
    quizHeaderProgressNone: 'None',
    quizHeaderProgressColor: 'Progress fill colour',
    quizHeaderTrackColor: 'Progress track colour',
    quizHeaderThickness: 'Bar thickness, px',
    quizHeaderBack: '“Back” button',
    quizHeaderBackIconBare: 'Plain',
    quizHeaderBackIconCircle: 'Outlined',
    quizHeaderBackPill: 'Pill',
    quizHeaderBackChevron: 'Chevron',
    quizHeaderBackCircleSolid: 'Round arrow',
    quizHeaderBackArrow: 'Arrow',
    quizHeaderGradientFrom: 'Bar gradient: from',
    quizHeaderGradientTo: 'Bar gradient: to',
    quizHeaderLayout: 'Layout',
    quizHeaderLayoutOneRow: 'One row',
    quizHeaderLayoutBarBelow: 'Bar below buttons',
    quizHeaderLayoutLogoRow: 'Back, logo, counter',
    quizHeaderButtonIconColor: 'Round button icon colour',
    quizHeaderLogoAlign: 'Logo position',
    quizHeaderLogoLeft: 'Left',
    quizHeaderLogoCenter: 'Centre',
    quizHeaderLogoRight: 'Right',
    quizHeaderLogoHeight: 'Logo height, px',
    quizHeaderBackNone: 'Hidden',
    quizHeaderClose: '“Close” cross',
    quizHeaderCloseIconBare: 'Plain',
    quizHeaderCloseIconCircle: 'In a circle',
    quizHeaderCloseCircleSolid: 'White circle',
    quizHeaderCloseNone: 'Hidden',
    quizHeaderCloseUrl: 'Cross link',
    quizHeaderCloseUrlHint: 'Without a link the cross is not shown.',
    quizHeaderCloseUrlInvalid: 'Use an http(s) link or a path on the site.',
    quizHeaderCounter: 'Step counter “2/14”',
    quizHeaderCounterOn: 'Show',
    quizHeaderCounterOff: 'Hide',
    quizHeaderCounterStyle: 'Counter look',
    quizHeaderCounterStylePlain: 'Plain',
    quizHeaderCounterStyleAccent: 'Current step in progress colour',
    quizHeaderCounterFontSize: 'Counter size, px',
    quizHeaderCounterFontWeight: 'Counter weight',
    quizHeaderCounterTotal: 'Counter total',
    quizHeaderCounterTotalHint: 'Empty — counted from the funnel screens. Changes only the number after the slash; the progress bar and analytics stay as counted.',
    quizHeaderPaddingTop: 'Top padding',
    quizHeaderPaddingX: 'Side padding',
    quizHeaderRowGap: 'Row gap',
    quizHeaderCounterColor: 'Counter colour',
    quizHeaderCounterCurrentColor: 'Current step colour',
    quizHeaderCounterTotalColor: 'Total colour',
    quizHeaderCounterSeparator: 'Counter separator',
    quizHeaderSeparatorSlash: 'Slash: 12/30',
    quizHeaderSeparatorSlashSpaced: 'Spaced slash: 12 / 30',
    quizHeaderSeparatorWord: 'Word: 9 of 15',
    quizHeaderForward: 'Forward button',
    quizHeaderForwardNone: 'None',
    quizHeaderForwardChevron: 'Chevron',
    quizHeaderNodeSize: 'Node size, px',
    quizHeaderLabelFontSize: 'Section label size, px',
    quizHeaderEndNode: 'End node',
    quizHeaderInsetRight: 'Right inset, px',
    quizHeaderProgressSectionBars: 'By section',
    quizHeaderSegmentGap: 'Segment gap, px',
    quizHeaderSegmentWeights: 'Segment proportions',
    quizHeaderSegmentWeightsHint: (b: {
      minItems: number;
      maxItems: number;
      min: number;
      max: number;
    }) =>
      `${b.minItems}–${b.maxItems} whole numbers ${b.min}–${b.max}, comma-separated (e.g. 3, 1). Empty — one segment per section.`,
    quizHeaderSegmentWeightsInvalid:
      'Proportions are not saved: enter whole numbers separated by commas, within the limits above.',
    // Слово владельца 21.10.2026: панель «Главная кнопка» — свои поля кнопки
    // экрана, перебивающие стиль (QuizCtaFields.tsx).
    quizCtaSection: 'Main button',
    quizCtaHint:
      'The “Next” button of the screen. Each field set here overrides the style; “As in the style” brings the style back.',
    quizCtaPlacement: 'Position',
    quizCtaPlacementFixedBottom: 'Pinned to the bottom',
    quizCtaPlacementInline: 'Under the content',
    quizCtaDisabledStyle: 'Look until the answer is given',
    quizCtaDisabledMutedBrand: 'Faded brand colour',
    quizCtaDisabledGrey: 'Grey',
    quizCtaDisabledMutedBrandDark: 'Faded and darkened',
    quizCtaDisabledOpacity: 'Opacity until the answer is given',
    quizCtaBottomOffset: 'Bottom offset of the pinned button',
    quizCtaResetAll: 'Whole button as in the style',
    // Слово владельца 21.10.2026: панель «Загрузчик» — свои поля экрана
    // обработки, перебивающие загрузчик стиля (QuizLoaderFields.tsx).
    quizLoaderSection: 'Loader',
    quizLoaderHint:
      'Look of the processing screen. Each field set here overrides the loader of the style. “As in the style” brings the style back.',
    quizLoaderAsInStyle: 'As in the style',
    quizLoaderResetAll: 'Whole loader as in the style',
    quizLoaderStyle: 'Look',
    quizLoaderStyleStackedStages: 'Stages one by one',
    quizLoaderStyleAllStages: 'All stages',
    quizLoaderStyleRing: 'Ring',
    quizLoaderStylePlainBar: 'Bar',
    quizLoaderPercent: 'Percent',
    quizLoaderPercentOn: 'Show',
    quizLoaderPercentOff: 'Hide',
    quizLoaderTitlePosition: 'Title',
    quizLoaderTitleAbove: 'Above indicator',
    quizLoaderTitleBelow: 'Below indicator',
    quizLoaderColor: 'Fill colour',
    quizLoaderTrackColor: 'Track colour',
    quizLoaderRingSize: 'Ring size, px (ring only)',
    quizLoaderRingStroke: 'Ring thickness, px (ring only)',
    // Задание stylepack/loader-block-look: та же панель у блока загрузки —
    // поле снимается до вида воронки.
    quizLoaderBlockSection: 'Loader look of this screen',
    quizLoaderBlockHint:
      'A set field overrides the funnel loader for this screen only; "As in the funnel" removes it.',
    quizLoaderAsInFunnel: 'As in the funnel',
    quizLoaderBlockResetAll: 'Whole loader as in the funnel',
    funnelLogo: 'Funnel logo',
    // WEB-2476 п.3: пометка у карточки стиля без логотипа в окне «Стиль оформления».
    stylePackNoLogo: 'no logo',
    // WEB-2476 п.3: выбранный стиль не рисует логотип — строка под полем.
    funnelLogoHiddenByStyle: (style: string) =>
      `The “${style}” style does not show the logo — pick another style to make the logo appear.`,
    funnelLogoHint:
      'Shown in the header of the funnel screens (unless the style hides it). ' +
      'A direct link to the image.',
    // Свой шрифт воронки (слово владельца 20.10.2026) — поле «Шрифт воронки».
    funnelFont: 'Funnel font',
    funnelFontHint:
      'A built-in font or one you uploaded — for all text of the funnel. ' +
      'Empty — as in the theme. A block can pick its own font in its properties.',
    customFontFamily: 'Font name',
    customFontWeight: 'Weight',
    customFontItalic: 'Italic',
    customFontUpload: 'Upload font file',
    customFontUploading: 'Uploading…',
    customFontFormatsHint: 'woff2, woff, ttf or otf, up to 4 MB. One file — one weight.',
    customFontWrongType: 'A font file is needed: woff2, woff, ttf or otf.',
    customFontTooLarge: 'The file is larger than 4 MB.',
    customFontBadFamily:
      'Font name: letters, digits, space, “-” and “_”, up to 48 characters; names of built-in fonts are taken.',
    customFontLimit: 'Up to 4 fonts with up to 8 weight and style variants each.',
    customFontFailed: 'Could not upload the font.',
    customFontFiles: (count: number) => `files: ${count}`,
    paletteSearchPlaceholder: (count: number) => `Search (${count} blocks)`,
    pickScreenFirst: 'Select a screen first',
    addBlockAria: (label: string) => `Add: ${label}`,
    // WEB-1321: правило в одну фразу — что показано, почему структура заперта
    // и куда идти за правкой перевода.
    translationStructureLocked: (lang: string) =>
      `You are viewing the ${lang.toUpperCase()} translation. Text is edited on any language — in the "Language" menu, "Edit the translation by hand". The structure (adding, deleting and reordering screens and blocks) is edited on the funnel source language only: pick it in the "Language" menu to go back.`,

    // ── Превью ──────────────────────────────────────────────────────────────
    previousScreen: 'Previous screen',
    nextScreen: 'Next screen',
    addBlockOrScreen: 'Add a block or a screen',
    newScreen: 'New screen',
    previewUnavailable: 'Preview unavailable',
    postPurchaseTestPurchase: 'Post-purchase screen · test purchase',
    screenHasNoBlocks: 'This screen has no blocks yet',
    // WEB-4111 (эталон editor-c-parts.jsx:159): кнопка плашки пустого экрана.
    screenEmptyAdd: 'Add a block',
    allBlocksHiddenPostPurchase:
      'Every block on this post-purchase screen is hidden',
    noScreensAddFirst:
      'No screens yet — add the first one in the “Layers” panel and the preview will appear here',
    // WEB-2057: подсказки автору внутри превью (redirect, авто-переход,
    // cookie-баннер, даунселл, адрес магазина, схема приложения) переехали в
    // отдельный словарь `authorHints.ts` — их рисует ОБЩИЙ рендерер блоков, и
    // ради восьми надписей покупателю воронки уезжал весь этот словарь.

    // ── Размеры рамки превью ────────────────────────────────────────────────
    phoneFrameRegular: 'Regular phone',
    phoneFrameNarrow: 'Narrow phone',
    phoneFrameLarge: 'Large phone',
    phoneFrameTablet: 'Tablet',

    // ── Полоса ветвления ────────────────────────────────────────────────────
    branchStripAria: 'Funnel branching',
    branchStripHint:
      'Funnel branching. A click switches the preview screen — this is not a ' +
      'run of the funnel: rule conditions and visitor answers are not evaluated here.',
    branchStripFork: (label: string) => `Fork: ${label}`,
    branchStripFallbackBranch: 'the “in order / otherwise” branch',
    branchFallbackTrigger: 'In order (otherwise)',
    branchFallbackSuffix: 'the natural order also leads here',
    branchUnknownTrigger: 'Transition',
    branchRule: 'Rule',
    branchRuleByAnswer: (question: string) =>
      `Rule: by answer to “${question}”`,
    branchOption: (label: string) => `Option “${label}”`,
    branchDeadEnd: (name: string) =>
      `Dead end: no exit of its own, the visitor falls through in order into the “${name}” branch`,

    // ── Экран заблокирован ролью ────────────────────────────────────────────
    editDeniedTitle: 'Editing is not available',
    editDeniedBody: (hint: string) =>
      `${hint}, so the editor is closed for you: the server would not accept ` +
      'the changes and the work would be lost. You can still see the lists of ' +
      'funnels, quizzes and payment screens. To get the right, ask the project ' +
      'owner or an administrator to change your role in the “Team” section.',

    // ── Индикатор сохранения ────────────────────────────────────────────────
    saving: 'Saving…',
    notSavedStale: 'Not saved — the page is out of date,',
    reloadPageLink: 'reload the page',
    andRetryEdit: 'and repeat the edit',
    notSavedDash: 'Not saved —',
    retryLink: 'retry',
    unsavedChanges: 'You have unsaved changes',
    saved: 'Saved',

    // ── Гейт публикации ─────────────────────────────────────────────────────
    noDomainDetail:
      'Without a domain only the preview is available (visible to the project team).',
    noDomainFunnelPartDetail:
      'Without a domain only the preview is available (visible to the project team). ' +
      'If this quiz or payment screen is part of a funnel with a bound domain, it is ' +
      'already reachable at the funnel address.',
    domainUnknownDetail:
      'We could not ask the server whether a domain is bound. Publishing is not ' +
      'blocked: if there really is no domain, the server will reject it itself.',
    domainUnknownBlocker: 'Could not check domains — run the check again',
    resourceReadonlyDetail:
      'This resource is over the limit of your current plan and switched to ' +
      'read-only: you can view it and show it in the preview, but not save or ' +
      'publish. Go back to the previous plan to edit again — and reload the ' +
      'editor page: the lock is lifted when the resource loads.',
    resourceReadonlyBlocker:
      'Locked by a plan downgrade — restore the previous plan',
    projectDebtDetail: (credits: number) =>
      `Outstanding balance of ${credits} credits — saving and publishing are paused until it is paid.`,
    projectDebtDetailNoAmount:
      'Outstanding balance — saving and publishing are paused until it is paid.',
    pricesStaleDetail: (stale: number) =>
      `That price no longer exists in the connected Stripe account (${stale} ${
        stale === 1 ? 'block' : 'blocks'
      }) — pick the prices again.`,
    pricesMissingDetail: (count: number) =>
      `${count} ${count === 1 ? 'block has' : 'blocks have'} no price.`,
    // WEB-3336: единственный «Выбор тарифа» стоит на экране после оплаты —
    // покупатель его до оплаты не видит, кнопке оплаты нечем списать деньги.
    pricesPostPurchaseSelectorDetail: (screen: string) =>
      `The plan selector is only on the post-purchase screen “${screen}” — buyers do not see it before paying, so the payment button has no price. Move the plan selector to a regular screen, or delete it and set a price on the payment button.`,
    blockerPayDebt: 'Pay off the outstanding balance',
    blockerBindDomain: 'Bind a domain and a subdomain',
    blockerConnectPayments: 'Connect a payment system in the project settings',
    blockerFillPrices: (count: number) =>
      `Fill in the prices (${count} ${count === 1 ? 'block' : 'blocks'} without a price)`,
    blockerMovePlanSelectorOffPostPurchase: (screen: string) =>
      `Move the plan selector off the post-purchase screen “${screen}”`,
    blockerResolvePriceConflicts: (count: number) =>
      `Resolve the price conflicts (${count})`,
    blockerAddContentBlock: 'Add at least one content block',

    // ── Юридические ссылки ──────────────────────────────────────────────────
    emptyTermsWarning:
      'The links to the terms of use and the privacy policy are empty.',
    placeholderTermsWarning:
      'The link points at a placeholder address (example.com and the like) — ' +
      'put in the address of your real document.',

    // ── Платёжная система ───────────────────────────────────────────────────
    paymentsNotConnectedHint:
      'No payment system is connected — payment will not work. ' +
      'Connect one in “Settings → Payments”.',

    // ── Конфликты цен на экране ─────────────────────────────────────────────
    // WEB-2057: тексты конфликтов цен собирает `pricingGate.ts` — модуль,
    // общий с рантаймом пейволла; они переехали в `authorHints.ts`.

    // ── Проверка экранов перед публикацией ──────────────────────────────────
    unnamedScreen: 'Untitled',
    confirmPublishTitleGeneric: 'Check the screens before publishing',
    confirmPublishTitleEmptyOne: 'A screen without content',
    confirmPublishTitleEmptyMany: 'Screens without content',
    confirmPublishTitleNoAdvanceOne: 'A screen without a navigation button',
    confirmPublishTitleNoAdvanceMany: 'Screens without a navigation button',
    confirmPublishEmptyPart: (names: string, count: number) =>
      `${names}: ${
        count === 1 ? 'the screen is empty' : 'the screens are empty'
      } — the visitor sees only a “Next” button.`,
    confirmPublishNoAdvancePart: (names: string, _count: number) =>
      `${names}: no navigation button — the visitor gets a fallback “Next”.`,
    // WEB-2126: наши платежи отключены, а блок оплаты остался.
    confirmPublishTitleOwnMoney: 'You take payments yourself',
    confirmPublishOwnMoneyPart: (count: number) =>
      `${
        count === 1
          ? 'There is a payment block'
          : `There are ${count} payment blocks`
      }, but payments through us are off — ${
        count === 1 ? 'the button' : 'the buttons'
      } will charge nothing.`,
    confirmPublishAnyway: 'Publish',
    confirmPublishBackToEditing: 'Back',
    confirmPublishQuestion: (body: string) => `${body} Publish?`,

    // ── История версий ──────────────────────────────────────────────────────
    versionHistory: 'Version history',
    versionsCreatedOnPublish: 'A version is created on every publish.',
    noSavedVersions: 'No saved versions',
    versionLabel: (number: number, isFirst: boolean) =>
      `v${number} · ${isFirst ? 'first publish' : 'publish'}`,
    // WEB-2131 (шаг 9): проверки и публикации — раздельно.
    versionsKindSwitchLabel: 'Which versions to show',
    versionsKindPublish: 'Publishes',
    versionsKindTest: 'Reviews',
    testVersionsHint:
      'A review is recorded every time you press “Send for review”. You ' +
      'cannot roll back to a review — rollback works only for publishes.',
    noTestVersions: 'No reviews yet',
    testVersionLabel: (number: number) => `Review #${number}`,
    versionBodyPurged: 'Contents were cleaned up — cannot be viewed',
    rollback: 'Roll back',
    rollbackConfirmTitle: (label: string) => `Roll back to ${label}?`,
    rollbackDraftWarning:
      'The current draft will be replaced with the contents of the selected ' +
      'version. Edits made after it will be lost.',
    rollbackPublishWarning:
      'The selected version will be published right away — the page starts ' +
      'accepting traffic again.',
    rollbackNoAppearanceWarning:
      'This version has no saved appearance (theme, palette, fonts) — the ' +
      'current page appearance stays after the rollback.',
    // WEB-2142: история грузится страницами по 20 — кнопка просит следующую.
    versionsLoadMore: 'Show more',
    versionsLoadFailed: 'Could not load the version history',
    versionsLoadMoreFailed: 'Could not load more versions. Try again.',
    versionRestored: 'Version restored',
    // WEB-2550: откат пускает право менять воронки (FUNNELS_WRITE), не только владельца.
    rollbackForbidden:
      'You have no permission to change funnels in this project — ask the project owner',
    rollbackNotOwner:
      'You can roll back only what you created yourself. Ask a project owner or admin.',
    rollbackFailed: 'Could not roll back',
    // WEB-2548: «Посмотреть» — просмотр версии до отката.
    versionPreview: 'View',
    versionPreviewBack: 'Back to versions',
    versionPreviewReadOnlyHint: 'Viewing a version does not change your draft.',
    versionPreviewLoadFailed: 'Could not load the version',
    versionPreviewNoAppearance:
      'This version has no saved appearance, so it is shown without a theme or ' +
      'palette. After a rollback the current page appearance stays.',
    versionPreviewEmpty: 'This version has no screens',
    versionPreviewPurged:
      'The contents of this version were cleaned up and cannot be viewed',
    versionPreviewScreenOf: (index: number, count: number) =>
      `Screen ${index} of ${count}`,
    versionPreviewPrevScreen: 'Previous screen',
    versionPreviewNextScreen: 'Next screen',
    versionPreviewRenderFailed:
      'Could not show this version: its saved contents are damaged. You can still go back to the list.',

    // ── Части воронки ───────────────────────────────────────────────────────
    partQuiz: 'Quiz',
    // WEB-3764 доводка холста: подпись эталона x.zones.pay (editor-c.jsx:151).
    partPaywall: 'Paywall',
    // WEB-3765: первый пункт переключателя частей — возврат на холст воронки.
    partMap: 'Map',
    // WEB-3764 доводка холста: отсутствующая часть в переключателе — «+ Пейволл»
    // / «+ Квиз» на месте пункта (эталон editor-c.jsx:1276).
    addPartInline: (part: string) => `+ ${part}`,
    funnelPartAddFailed: 'Could not add the funnel part. Please try again.',
    funnelPartCreateFailed:
      'Could not create the funnel part. Please try again.',
    funnelPartLinkFailed: (part: 'quiz' | 'paywall') =>
      `Could not link the funnel part: an unlinked ${
        part === 'quiz' ? 'quiz' : 'payment screen'
      } was created — delete it manually.`,

    // ── Режим показа пейволла ───────────────────────────────────────────────
    closeModeMandatory: 'Mandatory — cannot be closed',
    closeModeClosable: 'Closable — button right away',
    closeModeDelayed: 'Closable with a delay',
    closeModeShortMandatory: 'Display: mandatory',
    closeModeShortClosable: 'Display: closable',
    closeModeShortDelayed: 'Display: delayed',
    closeModeDrawerTitle: 'Payment screen display',
    closeModeDrawerHint:
      'How the payment screen behaves inside the app: whether the buyer can ' +
      'close it without paying. Nothing changes in a normal browser — the ' +
      'close button is not shown there.',
    closeModeMandatoryHint:
      'The buyer cannot leave the payment screen without paying. That is how every payment screen behaves today.',
    closeModeClosableHint: 'The close button is visible from the first second.',
    closeModeDelayedHint:
      'The close button appears once the set time has passed. Until then the payment screen is mandatory.',
    closeModeDelaySeconds: 'After how many seconds to show the button',

    // ── Галерея тем ─────────────────────────────────────────────────────────
    themeGalleryTitle: 'Theme',
    themeNone: 'No theme',
    themeNoneHint:
      'Remove the selected theme — go back to the default settings.',
    themeFilterAll: 'All',
    themeFilterDark: 'Dark',
    themeFilterLight: 'Light',
    themeFilterBright: 'Bright',
    themeSampleHeading: 'Heading',
    themeSampleText: 'Sample theme text',
    themeSampleButton: 'Button',

    // ── Галерея стиль-паков ─────────────────────────────────────────────────
    stylePackGalleryTitle: 'Style',
    stylePackNone: 'No style',
    // WEB-4151: «Оригинал» — размеры сайта у воронки из разбора по ссылке.
    stylePackOriginal: 'Original',
    stylePackOriginalHint: 'Sizes and shape as on the site',
    stylePackNoneHint:
      'Remove the selected style pack — go back to the default settings.',
    stylePackGalleryHint:
      'The style sets the shape of the funnel: corner radii, density, the type ' +
      'scale and the button behaviour. Colour stays with the palette — they combine.',
    stylePackSampleOption: 'Answer option',
    stylePackSampleOptionSecond: 'Second option',
    stylePackSampleNoButton: 'No button',

    // ── Названия и описания стиль-паков ─────────────────────────────────────
    stylePackSoftRoundedName: 'Soft rounded',
    stylePackSoftRoundedDescription:
      'Large corner radii, a thin brand border, a pill button at the bottom of the screen.',
    stylePackWarmCreamName: 'Warm cream',
    stylePackWarmCreamDescription:
      'Milky background, warm near-black text, large headings, ring selection.',
    stylePackCleanWhiteName: 'Clean white',
    stylePackCleanWhiteDescription:
      'White minimalism, a header with a step counter, a smooth screen fade-in.',
    stylePackFlatSharpName: 'Flat and strict',
    stylePackFlatSharpDescription:
      'Small radii and flat saturated colours — an “engineering” look without ornament.',
    stylePackCinematicPhotoName: 'Cinematic photo',
    stylePackCinematicPhotoDescription:
      'A full-screen photograph, white text on top, no interface around it.',
    stylePackDarkMysticName: 'Dark mystic',
    stylePackDarkMysticDescription:
      'Deep coloured background, a cream-gold serif, symbolic icons, ring selection.',
    stylePackEditorialSerifName: 'Magazine',
    stylePackEditorialSerifDescription:
      'Warm beige background, a regular-weight serif heading, a gapless list.',
    stylePackMonoGlamName: 'Black-and-white gloss',
    stylePackMonoGlamDescription:
      'Achromatic on a slightly tinted background, black plates, pill buttons.',
    stylePackBrightPopName: 'Bright accent',
    stylePackBrightPopDescription:
      'A saturated colour on white, a two-pixel brand border, plates with an arrow.',
    stylePackPastelSerifName: 'Pastel with a serif',
    stylePackPastelSerifDescription:
      'Pastel sections, a large coloured serif, a warm contrasting button.',
    stylePackTechMintName: 'Tech mint',
    stylePackTechMintDescription:
      'A dark accent card on a light page, a mint button, a two-tone heading.',
    stylePackProductPremiumName: 'Premium product',
    stylePackProductPremiumDescription:
      'The product photographed in a warm scene, a huge serif, a gold button, a promo strip.',
    stylePackBlueGradientName: 'Blue gradient',
    stylePackBlueGradientDescription:
      'Bright white screens, bold left-aligned questions, soft grey option cards and a blue gradient for the selected answer and buttons',
    stylePackSurveyFormName: 'Survey form',
    stylePackSurveyFormDescription:
      'A questionnaire: tall rows in a thin frame, a small question on the left, a plain button pinned to the bottom, a thin progress bar',
    stylePackOutlinePillsName: 'Outline pills',
    stylePackOutlinePillsDescription:
      'Long pill options in a thin frame with the text centred, a large bold question in the middle, a pill button, plenty of air',
    stylePackFramedCenterName: 'Framed center',
    stylePackFramedCenterDescription:
      'Options in a thin frame with 8 px corners, a bold centred question, a progress bar, the button pinned to the bottom',
    stylePackLeftSoftName: 'Soft left',
    stylePackLeftSoftDescription:
      'The question on the left, borderless options with 16 px corners, a pill button pinned to the bottom',
    stylePackPlainListName: 'Plain list',
    stylePackPlainListDescription:
      'Low borderless options, a centred question, a thin progress bar, the button right under the options',
    stylePackStepFormName: 'Step form',
    stylePackStepFormDescription:
      'A step-by-step form: framed options with 12 px corners, a centred question, segmented progress, the button at the bottom',

    // ── Оболочка редактора: шапка ───────────────────────────────────────────
    backToList: 'Back to the list',
    backToFunnels: 'Back to funnels',
    backToQuizzes: 'Back to quizzes',
    backToPaywalls: 'Back to payment screens',
    // WEB-2384 п.8 (пачка 48, U8): пять подписей верхней панели редактора
    // были зашиты по-английски и в русском кабинете читались как чужие.
    untitledFunnel: 'Untitled',
    previewButton: 'Preview',
    undoTooltip: 'Undo (Ctrl/Cmd+Z)',
    redoTooltip: 'Redo (Ctrl/Cmd+Shift+Z)',
    domainCheckFailed: 'Could not check the domains',
    domainBound: 'Domain bound',
    bindDomainAction: 'Bind a domain',
    domainButton: 'Domain',
    domainModalTitle: (name: string) => `Domain for “${name}”`,
    domainModalFunnelFallback: 'the funnel',
    fillPricesTooltip: 'Fill in the prices — a click jumps to the block',
    withoutPriceCount: (count: number) => `Without a price: ${count}`,
    // WEB-2325: строка над рамкой предпросмотра. Экран цен выглядит готовым
    // (цены нарисованы текстом), а к каталогу они не привязаны — публикация
    // откажет. Говорим это прямо на экране, а не только на кнопке публикации.
    previewUnpricedNotice:
      'No price is linked — the funnel cannot be published.',
    previewUnpricedStaleNotice:
      'That price no longer exists in the connected Stripe account — the funnel cannot be published.',
    previewUnpricedAction: 'Link a price',
    priceConflictsCount: (count: number) => `Price conflicts: ${count}`,
    publish: 'Publish',
    // WEB-2138, шаг приёмки 2: наведение говорит, кто увидит результат.
    publishTooltip: 'Goes live — all your ad traffic will see it',
    jumpToBlock: 'Jump to the block',
    retryAction: 'Retry',

    // ── Оболочка редактора: тосты и модалки ─────────────────────────────────
    quizSaved: 'Quiz saved',
    paywallSaved: 'Payment screen saved',
    quizPublished: 'Quiz published',
    paywallPublished: 'Payment screen published',
    notEnoughCreditsSave: 'Not enough credits. Top up to save.',
    notEnoughCreditsPublish: 'Not enough credits. Top up to publish.',
    pageChangedElsewhere:
      'The page was changed in another window — reload the editor',
    saveFailed: 'Could not save',
    saveRejected: 'Could not save: the server rejected the changes',
    publishFailed: 'Could not publish',
    // WEB-2138 ч.1: кнопка «На проверку» в шапке (подсказки — по макету шапки).
    // WEB-3767 доводка 2 шапки: подпись эталона ET.review (editor-c-top.jsx:64).
    sendToReview: 'Review',
    sendToReviewTooltip:
      'Freeze the current state — only people you gave the link to will see it',
    reviewBlockedAiTurn: 'The AI editor is working — wait until it finishes',
    reviewBlockedTranslation:
      'Translation mode: translations are not carried over to a review version',
    reviewBlockedSaving: 'The draft is being saved — wait a moment',
    reviewBlockedTurnDecision:
      'First answer the “The turn may already be saved on the server” dialog',
    reviewSent: 'Sent for review — the version is recorded',
    reviewFailed: 'Could not send for review',
    reviewNotOwner:
      'A member can send for review only what they created. Ask a project owner or admin.',
    // В2 (WEB-2138 п.5, WEB-2130): причины гашения, строка состояния проверки
    // и предупреждение в подтверждении публикации — по макету шапки.
    reviewBlockedReadonly:
      'Locked by a plan downgrade — restore the previous plan to send for review',
    // WEB-2845 п.3: без «пополните баланс» — при выключенной продаже пакетов
    // пополнить нечем; что делать с долгом, говорит плашка редактора.
    reviewBlockedDebt:
      'The organization has a credit debt — sending for review is closed until it is settled',
    reviewStateLabel: 'Review:',
    reviewStateNever: 'not sent yet',
    reviewStateChanged: 'there are changes since the last review',
    reviewStateLiveBehind: (date: string) =>
      `live is behind (published ${date})`,
    reviewStateLiveOk: 'live is up to date',
    reviewStateNotPublished: 'not published yet',
    // WEB-2595: all live funnels with this piece are paused. Not «visitors see nothing»:
    // the piece can still be served past a funnel (direct domain route, smart link, preview).
    reviewStateFunnelsPaused: 'all its funnels are paused',
    publishReviewChangedTitle:
      'You are publishing something your colleagues have not seen',
    publishReviewChangedBody: (date: string) =>
      `There are changes since the last review (${date}). This is allowed — just so you know the newest working state goes live.`,
    fillPricesBeforePublish: (count: number) =>
      `Fill in the prices before publishing: ${count} ${
        count === 1 ? 'block has' : 'blocks have'
      } no price`,
    stalePricesBeforePublish: (staleCount: number) =>
      `That price no longer exists in the connected Stripe account (${staleCount} ${
        staleCount === 1 ? 'block' : 'blocks'
      }) — pick the prices again before publishing`,
    draftRevertedOneStep: 'The draft is back at the previous step',
    revertTurnFailed: 'Could not undo the turn',
    revertTurnWaitPrevious:
      'The previous undo is still running — wait for it to finish',
    manualEditsReplaced:
      'Edits made by hand while the AI turn was running were replaced by its result.',
    draftChangedDuringTurn:
      'The draft changed while the turn was running — showing the fresh version; the turn result is in the chat history.',
    debtBanner: (credits: number) =>
      `Outstanding balance of ${credits} credits — saving and publishing are paused.`,
    topUp: 'Top up',
    // WEB-2312: тост «не хватает кредитов на перевод» (LanguageSwitcher) —
    // не про долг; у плашки долга своя фраза (billing.debtExitOwner, WEB-2845).
    topUpAskOwner: 'Ask the organization owner to top up the balance.',
    screenNumberLabel: (index: number, name: string) =>
      `screen ${index}${name ? ` “${name}”` : ''}`,
    // WEB-2882: правая колонка «Свойства | ИИ-редактор» и строка-переключатель слева.
    rightTabProperties: 'Properties',
    // WEB-3767 (сверка 30.10): вкладка чата — «AI», как эталон editor-c.jsx
    // x.tabs.ai; полное «AI editor» — шапка самого чата и подсказка вкладки.
    rightTabAi: 'AI',
    aiEditsCount: (count: number) =>
      `· ${count} ${plural('en', count, { one: 'edit', other: 'edits' })}`,
    clearBlockSelectionAria: 'Clear the block selection',
    pickBlockInLayers: 'Pick a block in the layers list or in the preview',
    themeCleared: 'Theme removed — default settings applied',
    themeApplied: (name: string) => `Theme “${name}” applied`,
    // WEB-2476 п.1: подтверждение темы поверх своих цветов воронки.
    themeReplaceColorsTitle: 'Replace the funnel colors?',
    themeReplaceColorsBody:
      'The theme will replace the button colors, background and text color currently set in the funnel. You can bring them back with “Undo” until the page is reloaded.',
    themeReplaceColorsBodyClear:
      'Removing the theme will also remove the button colors, background and text color currently set in the funnel. You can bring them back with “Undo” until the page is reloaded.',
    themeReplaceColorsApply: 'Apply theme',
    themeReplaceColorsClear: 'Remove theme',
    themeReplaceColorsKeep: 'Keep my colors',
    stylePackCleared: 'Style removed — default settings applied',
    stylePackTakeColors: "Use the style's colors",
    stylePackColorsTaken: 'Style colors applied',
    stylePackApplied: (name: string) => `Style “${name}” applied`,
    stylePackOriginalApplied: 'Style: Original — sizes as on the site',
    // WEB-3718: смена стиля и «взять цвета стиля» меняют тему — та же
    // проверка читаемости и тот же вопрос, что у галереи тем.
    stylePackAppliedWithRisk: (name: string, risk: string) =>
      `Style “${name}” applied. ${risk}`,
    stylePackColorsTakenWithRisk: (risk: string) =>
      `Style colors applied. ${risk}`,
    stylePackTakeColorsBody:
      'The style colors will replace the button colors, background and text color currently set in the funnel. You can bring them back with “Undo” until the page is reloaded.',
    contrastRiskAfterTheme: (count: number) =>
      `${count} ${
        count === 1
          ? 'block has its own background'
          : 'blocks have their own background'
      } where the text may become hard to read — please check it by hand.`,
    themeClearedWithRisk: (risk: string) => `Theme removed. ${risk}`,
    themeAppliedWithRisk: (name: string, risk: string) =>
      `Theme “${name}” applied. ${risk}`,

    // ── Язык части: переводы (переключатель шапки снят WEB-4206) ────────────
    addLanguage: 'Add language',
    translating: 'Translating…',
    translate: 'Translate',
    sourceLanguagePlaceholder: 'Pick the source language',
    deleteTranslation: 'Delete the translation',
    saveBeforeTranslate: 'Save your changes before translating.',
    saveBeforeSourceChange:
      'Save your changes before changing the source language.',
    changeSourceLanguageTitle: 'Change the source language',
    changeLanguageAction: 'Change language',
    sourceLanguageChanged: 'Source language changed',
    sourceLanguageChangeFailed: 'Could not change the source language',
    translationDeleteFailed: 'Could not delete the translation',
    topUpBalance: 'Top up the balance',
    // Окно смены исходного языка линзы «Языки» — три короткие строки эталона
    // (editor-c-langs.jsx SrcWin cs1–cs3; владелец 30.10, WEB-3767).
    sourceChangeTextsStay: (part: 'quiz' | 'paywall') =>
      `${part === 'quiz' ? 'Funnel' : 'Payment screen'} texts stay as they are`,
    sourceChangePlayerStrings:
      'Player strings — “Next”, “per month” — change right away',
    sourceChangeReorderTranslations: 'Order the existing translations again',
    sourceChangeBlockedByTranslation: (part: 'quiz' | 'paywall') =>
      `A translation of this ${
        part === 'quiz' ? 'funnel' : 'payment screen'
      } into this language has already been ordered. It cannot become the source: ` +
      'the original and the translation would be in the same language, and the ' +
      'paid translation would turn into dead weight — the visitor would no longer ' +
      'see it, while the catch-up run would keep maintaining it and charging ' +
      'credits for that. Delete the translation in this same menu and try again — ' +
      'or pick another language.',

    // ── Правка перевода ─────────────────────────────────────────────────────
    translationEditTitle: (language: string) =>
      `Edit the translation — ${language}`,
    translationSaveWithCount: (count: number) => `Save (${count})`,
    translationEditFree:
      'Editing is free — a re-translation would cost credits. Only the text ' +
      'changes, block settings are untouched.',
    translationChangedWhileOpen:
      'The translation changed while this window was open: the system caught up on the missing strings.',
    translationSaved: 'Translation saved',
    translationSaveFailed: 'Could not save the translation',
    translationMissingCount: (count: number) =>
      `The system counts ${count} strings as missing. They are marked below — it ` +
      'fills them in itself, editing them by hand is optional.',
    translationUnmatchedCount: (count: number) =>
      `${count} of them are not shown in this window (service fields or blocks that are already deleted).`,
    translationOnlyMissing: 'Missing only',
    translationNothingToEdit: 'The translation has no text to edit',
    translationMissingBadge: 'missing',

    // ── Выбор цены Stripe ───────────────────────────────────────────────────
    priceIntervalMonth: 'per month',
    priceIntervalYear: 'per year',
    priceIntervalWeek: 'per week',
    priceIntervalDay: 'per day',
    priceIntervalOneTime: 'one-time',
    createPriceTitle: 'Create a Stripe price',
    createPriceAction: 'Create',
    createPriceFailed: 'Could not create the price. Please try again.',
    productNameLabel: 'Product name',
    productNamePlaceholder: 'for example, Premium plan',
    amountLabel: 'Amount',
    currencyLabel: 'Currency',
    intervalLabel: 'Billing period',
    // WEB-2298 п.5: поле работает только для списаний через наше API.
    accessDaysLabel: 'Access length, days (API charges only, optional)',
    accessDaysPlaceholder: 'Empty — the server grants no access for this price',
    trialDaysLabel: 'Trial, days (optional)',
    trialDaysPlaceholder: 'No trial period',
    pricePlaceholder: 'Pick a Stripe price',
    priceGoneNotice: (withRefresh: boolean) =>
      'The selected price is no longer among the available ones: it was deleted in ' +
      'Stripe, or it is left over from previous keys. Payment with it will fail — ' +
      `pick a price again${withRefresh ? ' (the ↻ button refreshes the list)' : ''}.`,
    newPriceButton: '+ New price',
    stripeNotConnected: 'Stripe is not connected.',
    connectStripe: 'Connect Stripe',
    stripeConnectTail: 'to add prices, then import them here.',
    noStripePricesYet: 'No Stripe prices yet.',
    importFromStripe: 'Import from Stripe',
    // WEB-2438 Ж2а: вкладки «Stripe» нет — «Платежи»; импорт — в «Подписки → Товары».
    stripeImportHintPrefix: 'Connect Stripe in',
    stripeImportHintSettings: 'Project settings → Payments',
    stripeImportHintMiddle: 'and then in “Subscriptions → Products” press',
    stripeImportHintImport: 'Import from Stripe',
    stripeImportHintTail: '— then your prices show up here.',
    refreshPricesTooltip: 'Re-read the price list from Stripe',
    refreshPricesButton: '↻ Refresh from Stripe',
    // WEB-1714: явное стирание цены, сохранённой в запертом поле до того, как
    // в воронку поставили «Выбор тарифа».
    clearLockedPrice: 'Clear the saved price',

    // ── Портал подписчика: что видит автор в конструкторе ───────────────────
    portalInvalidLinkPreviewHint:
      'This is how the screen looks when the link is invalid: the buyer is invited ' +
      'to sign in by email. It is drawn by the portal block above it on the screen.',
    portalNothingToOfferHint:
      'There is nothing to offer (no matching subscription, or no discounted price ' +
      'selected) — the buyer does not see this block.',
    // WEB-1905: подпись-заглушка списка подписок на канвасе. Покупатель её
    // не видит никогда (у него список либо есть, либо печатается словарный
    // текст пустого состояния), поэтому язык — кабинета, а не воронки.
    portalSubscriptionListPreviewHint: "Subscriber's plans will appear here",

    // ── Адрес приложения ────────────────────────────────────────────────────
    destinationUrlTooLong: (max: number) =>
      `The URL is too long (max ${max} characters)`,
    destinationUrlInvalid: 'Enter a valid URL',
    destinationUrlNotHttps: 'The URL must start with https://',

    // ── Скретч-карта: текстуры покрытия ─────────────────────────────────────
    scratchCoverGoldFoil: 'Gold foil',
    scratchCoverSilverFoil: 'Silver',
    scratchCoverKraft: 'Kraft paper',
    scratchCoverDark: 'Dark gradient',

    // ── Отказы из состояния конструктора ────────────────────────────────────
    optionLimitRejected: (
      blockType: string,
      field: string,
      count: number,
      max: number,
    ) =>
      `The edit of the “${blockType}” block was rejected: field “${field}” — ` +
      `${count} items against a ceiling of ${max}. The list was left as it was: ` +
      'trimming it silently is not allowed (the items carry the author’s text and ' +
      'branching transitions).',

    // ── Пометки у типов блоков (WEB-2075) ───────────────────────────────────
    // Ключи с префиксом `blockNotice` адресуются из BLOCK_DEFINITIONS так же,
    // как `blockLabel`: подпись под названием в палитре и предупреждение в
    // панели свойств — ОДИН текст, иначе концы разъедутся. Промо-тикет пока
    // единственный такой блок: движка купонов в продукте нет, а поле выглядит
    // рабочим. Формулировка согласована с каталогом блоков для ИИ
    // (backend/src/ai-funnels/block-schema/block-catalog-prompt.ts), сторож
    // совпадения — promoTicketDecorationEnds.web2075.test.ts.
    blockNoticePromoTicket:
      'Decoration: the code is not validated and gives no discount.',
    // WEB-2451 п.6: блоки, которые работают только в приложении с SDK; тексты
    // согласованы с пояснениями панелей (editorPanel.restorePurchasesText,
    // authorHints.closeWebviewOnlyInApp).
    blockNoticeRestorePurchases:
      'Only in an app with the SDK: hidden in a plain browser.',
    blockNoticeCloseWebview:
      'For an app with the SDK: in a plain browser it is shown only when a custom link is set.',

    // ── WEB-2116 / WEB-2138: окно «Поделиться» и меню под стрелкой «На проверку»
    // (макет builder-mockup: SharePreviewModal, ReviewSettingsMenu). Тексты —
    // из макета один в один (решение владельца 06.10: «меньше текста» было
    // только про окно из таблицы воронок, оно в словаре projects).
    shareButton: 'Share',
    shareButtonTooltip: 'The review link and issued links',
    shareNoEditRight: 'Available to those who can edit the funnel',
    shareModalTitleQuiz: 'Share a preview of the quiz',
    shareModalTitlePaywall: 'Share a preview of the paywall',
    shareModalTitleFunnel: 'Share a preview of the funnel',
    shareNote:
      'The recipient sees the version sent for review. Not in reports, cannot pay.',
    shareNoteHint:
      'The link address stays the same when you send new versions for review — no need to send it again. The access code and version behaviour are also set in the editor header, under the arrow next to “Send for review”.',
    shareNoteHintAria: 'More about the link',
    shareTtlLabel: (max: number) => `Valid for, hours (1–${max})`,
    shareTtlPreset: (hours: number) => `${hours} h`,
    shareTtlTooLong: (max: number) => `Maximum ${max} hours — enter less.`,
    shareTtlMissing: (max: number) => `Enter from 1 to ${max} hours.`,
    shareCreate: 'Create',
    shareCodeGenerate: 'Generate code',
    shareCodeRegenerate: 'Generate another code',
    shareCodeRemove: 'Remove the code',
    // Настройки кода у квиза, пейволла и воронки раздельные — подсказка
    // называет свой объект (как `shareLinksTitleQuiz/Paywall/Funnel`).
    shareCodeSharedHint: 'One code for all links of this funnel',
    shareCodeSharedHintQuiz: 'One code for all links of this quiz',
    shareCodeSharedHintPaywall: 'One code for all links of this paywall',
    shareCreatedLabel: 'Link',
    shareUrlUnavailable:
      'The link address is unavailable — please contact support',
    shareValidUntil: (date: string) => `Valid until ${date}`,
    shareCreatedCode: 'Access code:',
    shareCodeSendSeparately:
      'Send the code in a separate message, not together with the link',
    shareCopy: 'Copy',
    shareCopyLinkTooltip: 'Copy link',
    shareLinkCopied: 'Link copied',
    shareCodeCopied: 'Code copied',
    shareCopyFailed: 'Could not copy — select the text and copy it by hand',
    // WEB-2800: копирование из строки таблицы ссылок не удалось — адрес открыт под строкой.
    shareCopyFailedRowShown: 'Could not copy — the link address is now shown under its row: select it and copy it by hand',
    shareManualCopyHint: 'Select the address and copy it by hand:',
    shareLinksTitleQuiz: 'Links to this quiz',
    shareLinksTitlePaywall: 'Links to this paywall',
    shareLinksTitleFunnel: 'Links to this funnel',
    shareLinksEmpty: 'No links yet',
    shareLinksLoadFailed: 'Could not load the links',
    shareRetry: 'Retry',
    shareColCreated: 'Created',
    shareColExpires: 'Expires',
    shareColVersions: 'Versions',
    shareColCode: 'Code',
    shareColOpens: 'Opens',
    shareExpiredMark: 'expired',
    shareCodeYes: 'yes',
    shareCodeNo: 'no code',
    // WEB-2788: код включён, а сохранённый не расшифровался (сменили ключ, испорчена запись).
    shareCodeBroken: 'create the code again',
    shareCodeBrokenHint: 'The code is damaged, recipients cannot get in',
    shareCodeGenerateNew: 'Generate a new code',
    shareRevoke: 'Revoke',
    shareRevokeConfirm:
      'Revoke the link? It stops opening for everyone at once.',
    shareCancel: 'Cancel',
    shareRevoked: 'Revoked',
    shareRevokedToast:
      'Link revoked — it stopped opening at once for everyone you sent it to',
    sharePlanStopped: 'stopped by the plan',
    // WEB-2789: квиз или экран оплаты воронки удалён — получатель видит отказ.
    shareFunnelBrokenStopped:
      'stopped: the funnel is broken (its quiz or paywall was deleted)',
    // WEB-3364: причина остановки, которой кабинет не знает, — общая подпись.
    shareOtherStopped: 'stopped — the link does not open',
    shareCreateFailed: 'Could not create the link',
    shareRevokeFailed: 'Could not revoke the link',
    // WEB-3749, юнит «предпросмотр»: окно «Предпросмотр» таблиц (эталон content-dlg.jsx SH).
    sharePvTitle: (name: string) => `Preview — ${name}`,
    shareSelf: 'See it yourself',
    shareSelfHint: 'The current draft — in a new tab',
    shareOpenSelf: 'Open',
    shareOther: 'A link for someone else',
    shareDone: 'Done',
    shareRevokeYes: 'Yes, revoke',
    sharePagePrev: 'Previous',
    sharePageNext: 'Next',
    reviewSettingsArrowTooltip: 'Review settings: link, access code, versions',
    reviewLinkTitle: 'Link to view',
    reviewLinkHint:
      'Send it to whoever should take a look. The address stays the same when you send new reviews.',
    reviewLinkCreate: (hours: number) => `Create a link for ${hours} h`,
    reviewSettingsTitle: 'Review settings',
    reviewSettingsHint: 'Apply to every link of this funnel.',
    reviewSettingsHintQuiz: 'Apply to every link of this quiz.',
    reviewSettingsHintPaywall: 'Apply to every link of this paywall.',
    reviewAskCode: 'Ask for an access code',
    reviewGenCode: 'Generate a code: no zero and O, no one and I',
    reviewCodeBroken:
      'The saved code is damaged (the encryption key changed or the record is corrupted): recipients are asked for a code nobody can enter. Generate a new code.',
    reviewCopyCode: 'Copy code',
    reviewCodeInvalid: '6 characters: letters and digits without 0, O, 1, I',
    reviewVersions: 'Versions',
    reviewFirstTimeNote:
      'This panel opened by itself because it is your first review. Next time use the arrow.',
    reviewSettingsLoadFailed: 'Could not load the review settings',
    reviewSettingsSaveFailed: 'Could not save the review settings',
    reviewCodeGenerateFailed: 'Could not generate a code',
    // WEB-2560: отказ сохранения при уходе из редактора — в корневом держателе.
    leaveSaveLostStale: (name: string) =>
      `Your last edits${name.trim() ? ` to “${name.trim()}”` : ''} were not saved: the draft was changed in another window or by another member. Open it again to check.`,
    leaveSaveLost: (name: string) =>
      `Your last edits${name.trim() ? ` to “${name.trim()}”` : ''} could not be saved. Open it again to check.`,
  },
  {
    blockLabelTitle: 'Заголовок',
    blockLabelSubTitle: 'Подзаголовок',
    blockLabelHeading: 'Заголовок раздела',
    blockLabelParagraph: 'Абзац',
    blockLabelImage: 'Изображение',
    blockLabelHtml: 'HTML',
    blockLabelSingleChoice: 'Один вариант',
    blockLabelMultipleChoice: 'Несколько вариантов',
    blockLabelLikertScale: 'Шкала эмодзи (лайкерт)',
    blockLabelMicroFeedback: 'Микро-фидбек на ответ',
    blockLabelStatementAgreement: 'Утверждение с реакцией (✕/🤔/✓)',
    blockLabelPreferenceCard: 'Карточка предпочтения (да/нет)',
    blockLabelEmail: 'Email',
    blockLabelTextField: 'Текстовое поле',
    blockLabelCheckbox: 'Чекбокс',
    blockLabelButtonNext: 'Кнопка «Далее»',
    blockLabelButtonLink: 'Кнопка-ссылка',
    blockLabelRedirectUrl: 'Редирект на URL',
    blockLabelProgressBar: 'Полоса прогресса',
    blockLabelSpinner: 'Спиннер',
    blockLabelResultScreen: 'Экран результата',
    blockLabelPriceBlock: 'Блок цены',
    blockLabelButtonToPay: 'Кнопка покупки',
    blockLabelReturnToApp: 'Кнопка возврата в приложение',
    blockLabelStripeWalletsExpress: 'Экспресс-кошельки',
    blockLabelTermsCheckbox: 'Чекбокс условий',
    blockLabelSensitiveConsent: 'Явное согласие на чувствительные данные',
    sensitiveConsentNicheHintTitle:
      'В этой нише часто спрашивают о чувствительном',
    sensitiveConsentNicheHintText: (blockLabel: string) =>
      `Диагнозы, аллергии, ориентация, семейное положение. Если такие ` +
      `вопросы в воронке есть — поставьте блок «${blockLabel}» перед первым ` +
      `из них: без явного согласия такие ответы собирать нельзя. Если таких ` +
      `вопросов нет — добавлять ничего не нужно.`,
    blockLabelPaymentBanners: 'Баннеры платёжных систем',
    blockLabelPriceBreakdownText: 'Цена по частям (в день)',
    blockLabelRestorePurchases: 'Восстановить покупки',
    blockLabelCloseWebview: 'Кнопка «Закрыть окно»',
    blockLabelSubscriptionList: 'Портал: список подписок',
    blockLabelCancelButton: 'Портал: кнопка отмены',
    blockLabelResumeButton: 'Портал: кнопка возобновления',
    blockLabelDownsellOffer: 'Портал: оффер удержания (скидка)',
    // WEB-2090 — пара к английской половине выше.
    blockLabelTimer: 'Время (статичная надпись)',
    blockLabelDivider: 'Разделитель',
    blockLabelSpacer: 'Отступ',
    blockLabelIconWithText: 'Иконка + текст',
    blockLabelBulletList: 'Маркированный список',
    blockLabelNumberedList: 'Нумерованный список',
    blockLabelAccordion: 'Аккордеон',
    blockLabelColumns2: '2 колонки',
    blockLabelCard: 'Карточка',
    blockLabelPhoneInput: 'Поле телефона',
    blockLabelDatePicker: 'Выбор даты',
    blockLabelNumberInput: 'Числовое поле',
    blockLabelSelectDropdown: 'Выпадающий список',
    blockLabelRatingStars: 'Рейтинг звёздами',
    blockLabelSliderRange: 'Ползунок',
    blockLabelToggle: 'Переключатель',
    blockLabelNameField: 'Поле имени',
    blockLabelVideoEmbed: 'Видео',
    blockLabelTestimonials: 'Отзывы',
    blockLabelFeatureChips: 'Плашки-фичи',
    blockLabelReviewStars: 'Рейтинг отзывов',
    blockLabelRatingChip: 'Чип рейтинга (соцпруф)',
    blockLabelTrustBadges: 'Значки доверия',
    blockLabelGuaranteeBadge: 'Значок гарантии',
    blockLabelBeforeAfter: 'До / После',
    blockLabelGallery: 'Галерея',
    blockLabelCountdown: 'Обратный отсчёт',
    blockLabelStockBadge: 'Значок «в наличии»',
    blockLabelUrgencyBanner: 'Баннер срочности',
    blockLabelCallout: 'Врезка',
    blockLabelScratchCard: 'Скретч-карта скидки',
    blockLabelBlurTeaser: 'Тизер с замочками',
    blockLabelForecastChart: 'График-прогноз',
    blockLabelPromoTicket: 'Промо-тикет с кодом',
    blockLabelNowGoalBars: 'Сравнение «сейчас / цель»',
    blockLabelBmiScale: 'Шкала ИМТ с ползунком',
    blockLabelProgressText: 'Текст прогресса',
    blockLabelConditionalBranch: 'Условное ветвление',
    blockLabelMultiStepQuestion: 'Многошаговый вопрос',
    blockLabelImageChoice: 'Выбор по изображению',
    blockLabelWeightPicker: 'Выбор веса',
    blockLabelHeightPicker: 'Выбор роста',
    blockLabelPlanSelector: 'Выбор тарифа',
    blockLabelPaymentTerms: 'Условия оплаты',
    blockLabelDownsellPopup: 'Попап-даунселл',
    blockLabelLoaderProcessing: 'Загрузка / Обработка',
    blockLabelChart: 'График',
    blockLabelCookieConsent: 'Согласие на cookie',
    blockLabelPhotoUpload: 'Загрузка фото',
    // WEB-2438 Ж13 (решение владельца 2438-Р2): типы вне палитры — в «Слоях».
    offPaletteLabelHero: 'Обложка',
    offPaletteLabelStripeCheckoutForm: 'Кнопка оплаты (старая)',

    blockCategoryText: 'Текст',
    blockCategoryMedia: 'Медиа',
    blockCategoryQuestions: 'Вопросы',
    blockCategoryLeads: 'Сбор лидов',
    blockCategoryNavigation: 'Навигация',
    blockCategoryVisual: 'Визуал',
    blockCategoryStructure: 'Структура',
    blockCategoryPayment: 'Оплата',
    blockCategoryPortal: 'Портал подписки',
    blockCategoryUrgency: 'Срочность',
    blockCategoryLayout: 'Макет и визуал',
    blockCategoryForms: 'Формы',
    blockCategorySocialProof: 'Медиа и соцдоказательства',
    blockCategoryUrgencyConversion: 'Срочность и конверсия',
    blockCategoryQuizLogic: 'Логика квиза',

    addBlockTitle: 'Добавить блок',
    blockHiddenOnPostPurchase:
      'На экране после оплаты этот блок не показывается',

    layersTab: 'Слои',
    blocksTab: 'Блоки',
    addScreen: 'Добавить экран',
    addFirstScreen: 'Добавить первый экран',
    deleteScreen: 'Удалить экран',
    deleteBlock: 'Удалить блок',
    // WEB-2704: «Дублировать» экран и блок в «Слоях».
    duplicateScreen: 'Дублировать экран',
    duplicateBlock: 'Дублировать блок',
    // WEB-4111 (эталон editor-c.jsx:1299, i2.aiBlock): «ИИ» в строке блока «Слоёв».
    layerBlockAi: 'ИИ',
    screenCopySuffix: ' (копия)',
    screenDuplicated: 'Экран скопирован — копия стоит сразу за исходным',
    screenDuplicatedLogicStays: (name: string) =>
      `Экран скопирован. Правила «Логики» остались на «${name}», у копии их нет.`,
    blockDuplicated: 'Блок скопирован — копия под исходным',
    noScreensYet: 'Пока нет экранов',
    screenIsOneStep: 'Экран — это один шаг, который видит пользователь',
    postPurchaseScreen: 'Экран после оплаты',
    postPurchaseNotShown: 'Не показывается на экране после оплаты',
    priceMissingHint: 'Не задана цена — нужно заполнить перед публикацией',
    imageNotGeneratedHint:
      'Картинка не сгенерировалась — откройте блок и нажмите «Сгенерировать заново»',
    screenImageNotGeneratedHint:
      'На этом экране картинка не сгенерировалась — её можно сгенерировать заново на блоке картинки',
    screenHasNoAdvanceLabel: 'На экране нет кнопки перехода',
    noAdvanceHint:
      'На этом экране нет кнопки перехода. На опубликованной странице ' +
      'посетителю будет показана запасная кнопка «Далее». Это не ошибка — ' +
      'можно добавить свой блок «Кнопка Далее» или оставить как есть.',
    // WEB-3767 (скрины 30.10): одна короткая строка вместо абзаца
    postPurchaseNotChosen: 'Свой экран после оплаты не выбран — покажем стандартный',
    paywallStyleNoteGeneric:
      'Внутри воронки экран с ценами берёт стиль квиза, если он у квиза выбран. Стиль, выбранный здесь, действует, когда у квиза стиля нет или экран с ценами открыт отдельно.',
    paywallStyleNoteQuizPack: (style: string) =>
      `В этой воронке действует стиль квиза «${style}» — стиль, выбранный здесь, действует, только когда экран с ценами открыт отдельно.`,
    quizHeaderSection: 'Шапка квиза',
    quizHeaderHint:
      'Каждое заданное здесь поле перебивает шапку стиля. «Как в стиле» возвращает вид стиля.',
    quizHeaderAsInStyle: 'Как в стиле',
    quizHeaderResetAll: 'Вся шапка как в стиле',
    quizHeaderProgress: 'Прогресс',
    quizHeaderProgressBar: 'Полоса',
    quizHeaderProgressSegmented: 'Сегменты',
    quizHeaderProgressNodes: 'Точки',
    quizHeaderProgressSectionSteps: 'Шаги-разделы',
    quizHeaderProgressNone: 'Нет',
    quizHeaderProgressColor: 'Цвет заполнения прогресса',
    quizHeaderTrackColor: 'Цвет дорожки прогресса',
    quizHeaderThickness: 'Толщина полосы, px',
    quizHeaderBack: 'Кнопка «Назад»',
    quizHeaderBackIconBare: 'Без рамки',
    quizHeaderBackIconCircle: 'В рамке',
    quizHeaderBackPill: 'Пилюля',
    quizHeaderBackChevron: 'Шеврон',
    quizHeaderBackCircleSolid: 'Круглая со стрелкой',
    quizHeaderBackArrow: 'Стрелка',
    quizHeaderGradientFrom: 'Градиент полосы: от',
    quizHeaderGradientTo: 'Градиент полосы: до',
    quizHeaderLayout: 'Раскладка',
    quizHeaderLayoutOneRow: 'В одну строку',
    quizHeaderLayoutBarBelow: 'Полоса под кнопками',
    quizHeaderLayoutLogoRow: '«Назад», логотип, счётчик',
    quizHeaderButtonIconColor: 'Цвет значка круглых кнопок',
    quizHeaderLogoAlign: 'Положение логотипа',
    quizHeaderLogoLeft: 'Слева',
    quizHeaderLogoCenter: 'По центру',
    quizHeaderLogoRight: 'Справа',
    quizHeaderLogoHeight: 'Высота логотипа, px',
    quizHeaderBackNone: 'Скрыта',
    quizHeaderClose: 'Крестик «Закрыть»',
    quizHeaderCloseIconBare: 'Без фона',
    quizHeaderCloseIconCircle: 'В круге',
    quizHeaderCloseCircleSolid: 'Белый круг',
    quizHeaderCloseNone: 'Скрыт',
    quizHeaderCloseUrl: 'Адрес крестика',
    quizHeaderCloseUrlHint: 'Без адреса крестик не показывается.',
    quizHeaderCloseUrlInvalid: 'Нужен адрес http(s) или путь на сайте.',
    quizHeaderCounter: 'Счётчик шагов «2/14»',
    quizHeaderCounterOn: 'Показывать',
    quizHeaderCounterOff: 'Скрыть',
    quizHeaderCounterStyle: 'Вид счётчика',
    quizHeaderCounterStylePlain: 'Обычный',
    quizHeaderCounterStyleAccent: 'Номер цветом полосы',
    quizHeaderCounterFontSize: 'Кегль счётчика, px',
    quizHeaderCounterFontWeight: 'Жирность счётчика',
    quizHeaderCounterTotal: 'Всего шагов в счётчике',
    quizHeaderCounterTotalHint: 'Пусто — по числу экранов воронки. Меняет только число после косой черты; полоса прогресса и аналитика считаются как прежде.',
    quizHeaderPaddingTop: 'Отступ сверху',
    quizHeaderPaddingX: 'Поля по бокам',
    quizHeaderRowGap: 'Между рядами',
    quizHeaderCounterColor: 'Цвет счётчика',
    quizHeaderCounterCurrentColor: 'Цвет текущего номера',
    quizHeaderCounterTotalColor: 'Цвет «всего»',
    quizHeaderCounterSeparator: 'Разделитель счётчика',
    quizHeaderSeparatorSlash: 'Черта: 12/30',
    quizHeaderSeparatorSlashSpaced: 'Черта с пробелами: 12 / 30',
    quizHeaderSeparatorWord: 'Словом: 9 из 15',
    quizHeaderForward: 'Кнопка «вперёд»',
    quizHeaderForwardNone: 'Нет',
    quizHeaderForwardChevron: 'Шеврон',
    quizHeaderNodeSize: 'Размер узла, px',
    quizHeaderLabelFontSize: 'Размер подписи раздела, px',
    quizHeaderEndNode: 'Конечный узел',
    quizHeaderInsetRight: 'Отступ справа, px',
    quizHeaderProgressSectionBars: 'По разделам',
    quizHeaderSegmentGap: 'Зазор отрезков, px',
    quizHeaderSegmentWeights: 'Пропорции отрезков',
    quizHeaderSegmentWeightsHint: (b: {
      minItems: number;
      maxItems: number;
      min: number;
      max: number;
    }) =>
      `${b.minItems}–${b.maxItems} целых чисел ${b.min}–${b.max} через запятую (например, 3, 1). Пусто — отрезок на раздел.`,
    quizHeaderSegmentWeightsInvalid:
      'Пропорции не сохранены: нужны целые числа через запятую в границах выше.',
    quizCtaSection: 'Главная кнопка',
    quizCtaHint:
      'Кнопка «Далее» экрана. Каждое заданное здесь поле перебивает стиль; «Как в стиле» возвращает вид стиля.',
    quizCtaPlacement: 'Положение',
    quizCtaPlacementFixedBottom: 'Прибита к низу',
    quizCtaPlacementInline: 'Под содержимым',
    quizCtaDisabledStyle: 'Вид, пока не дан ответ',
    quizCtaDisabledMutedBrand: 'Бледный цвет бренда',
    quizCtaDisabledGrey: 'Серая',
    quizCtaDisabledMutedBrandDark: 'Бледная с затемнением',
    quizCtaDisabledOpacity: 'Прозрачность, пока не дан ответ',
    quizCtaBottomOffset: 'Отступ снизу у прибитой кнопки',
    quizCtaResetAll: 'Вся кнопка как в стиле',
    quizLoaderSection: 'Загрузчик',
    quizLoaderHint:
      'Вид экрана обработки. Каждое заданное здесь поле перебивает загрузчик стиля. «Как в стиле» возвращает вид стиля.',
    quizLoaderAsInStyle: 'Как в стиле',
    quizLoaderResetAll: 'Весь загрузчик как в стиле',
    quizLoaderStyle: 'Вид',
    quizLoaderStyleStackedStages: 'Стадии по одной',
    quizLoaderStyleAllStages: 'Все стадии',
    quizLoaderStyleRing: 'Кольцо',
    quizLoaderStylePlainBar: 'Полоса',
    quizLoaderPercent: 'Процент',
    quizLoaderPercentOn: 'Показывать',
    quizLoaderPercentOff: 'Скрыть',
    quizLoaderTitlePosition: 'Подпись',
    quizLoaderTitleAbove: 'Над индикатором',
    quizLoaderTitleBelow: 'Под индикатором',
    quizLoaderColor: 'Цвет заполнения',
    quizLoaderTrackColor: 'Цвет дорожки',
    quizLoaderRingSize: 'Размер кольца, px (только кольцо)',
    quizLoaderRingStroke: 'Толщина кольца, px (только кольцо)',
    // Задание stylepack/loader-block-look: та же панель у блока загрузки.
    quizLoaderBlockSection: 'Вид загрузчика этого экрана',
    quizLoaderBlockHint:
      'Заданное поле перебивает загрузчик воронки только на этом экране; «Как у воронки» снимает его.',
    quizLoaderAsInFunnel: 'Как у воронки',
    quizLoaderBlockResetAll: 'Весь загрузчик как у воронки',
    funnelLogo: 'Логотип воронки',
    stylePackNoLogo: 'без логотипа',
    funnelLogoHiddenByStyle: (style: string) =>
      `Стиль «${style}» логотип не показывает — выберите другой стиль, чтобы логотип появился.`,
    funnelLogoHint:
      'Показывается в шапке экранов воронки (если стиль его не прячет). ' +
      'Прямая ссылка на картинку.',
    // Свой шрифт воронки (слово владельца 20.10.2026) — поле «Шрифт воронки».
    funnelFont: 'Шрифт воронки',
    funnelFontHint:
      'Встроенный шрифт или загруженный вами — для всего текста воронки. ' +
      'Пусто — как у темы. Блоку можно выбрать свой шрифт в его свойствах.',
    customFontFamily: 'Название шрифта',
    customFontWeight: 'Жирность',
    customFontItalic: 'Курсив',
    customFontUpload: 'Загрузить файл шрифта',
    customFontUploading: 'Загружаем…',
    customFontFormatsHint: 'woff2, woff, ttf или otf, до 4 МБ. Один файл — одна жирность.',
    customFontWrongType: 'Нужен файл шрифта: woff2, woff, ttf или otf.',
    customFontTooLarge: 'Файл больше 4 МБ.',
    customFontBadFamily:
      'Название шрифта: буквы, цифры, пробел, «-» и «_», до 48 знаков; имена встроенных шрифтов заняты.',
    customFontLimit: 'Не больше 4 шрифтов и 8 начертаний в каждом.',
    customFontFailed: 'Не удалось загрузить шрифт.',
    customFontFiles: (count: number) => `файлов: ${count}`,
    paletteSearchPlaceholder: (count: number) => `Поиск (${count} блоков)`,
    pickScreenFirst: 'Сначала выберите экран',
    addBlockAria: (label: string) => `Добавить: ${label}`,
    // WEB-1321: правило в одну фразу — что показано, почему структура заперта
    // и куда идти за правкой перевода.
    translationStructureLocked: (lang: string) =>
      `Показан перевод на ${lang.toUpperCase()}. Текст правится на любом языке — в меню «Язык», пункт «Править перевод вручную». Структура (добавление, удаление и перестановка экранов и блоков) правится только на исходном языке воронки: выберите его в меню «Язык», чтобы вернуться.`,

    previousScreen: 'Предыдущий экран',
    nextScreen: 'Следующий экран',
    addBlockOrScreen: 'Добавить блок или экран',
    newScreen: 'Новый экран',
    previewUnavailable: 'Предпросмотр недоступен',
    postPurchaseTestPurchase: 'Экран после оплаты · тестовая покупка',
    screenHasNoBlocks: 'На экране пока нет блоков',
    // WEB-4111 (эталон editor-c-parts.jsx:159): кнопка плашки пустого экрана.
    screenEmptyAdd: 'Добавить блок',
    allBlocksHiddenPostPurchase:
      'Все блоки этого экрана после оплаты не показываются',
    noScreensAddFirst:
      'Нет экранов — добавьте первый в панели «Слои», и превью появится здесь',
    // WEB-2057: подсказки автору внутри превью (redirect, авто-переход,
    // cookie-баннер, даунселл, адрес магазина, схема приложения) переехали в
    // отдельный словарь `authorHints.ts` — их рисует ОБЩИЙ рендерер блоков, и
    // ради восьми надписей покупателю воронки уезжал весь этот словарь.

    phoneFrameRegular: 'Обычный телефон',
    phoneFrameNarrow: 'Узкий телефон',
    phoneFrameLarge: 'Крупный телефон',
    phoneFrameTablet: 'Планшет',

    branchStripAria: 'Ветвление воронки',
    branchStripHint:
      'Ветвление воронки. Клик переключает экран превью — это не прохождение ' +
      'воронки: условия правил и ответы посетителя тут не проверяются.',
    branchStripFork: (label: string) => `Развилка: ${label}`,
    branchStripFallbackBranch: 'ветка «по порядку/иначе»',
    branchFallbackTrigger: 'По порядку (иначе)',
    branchFallbackSuffix: 'сюда же ведёт порядок (иначе)',
    branchUnknownTrigger: 'Переход',
    branchRule: 'Правило',
    branchRuleByAnswer: (question: string) =>
      `Правило: по ответу «${question}»`,
    branchOption: (label: string) => `Вариант «${label}»`,
    branchDeadEnd: (name: string) =>
      `Тупик: своего выхода нет, посетитель провалится по порядку в ветку «${name}»`,

    editDeniedTitle: 'Редактирование недоступно',
    editDeniedBody: (hint: string) =>
      `${hint}, поэтому конструктор для вас закрыт: сервер не примет ` +
      'изменения, и работа пропала бы. Списки воронок, квизов и экранов ' +
      'оплаты вам по-прежнему видны. Чтобы получить право, попросите ' +
      'владельца или администратора проекта изменить вашу роль в разделе «Команда».',

    saving: 'Сохранение…',
    notSavedStale: 'Не сохранено — страница устарела,',
    reloadPageLink: 'обновите страницу',
    andRetryEdit: 'и повторите правку',
    notSavedDash: 'Не сохранено —',
    retryLink: 'повторить',
    unsavedChanges: 'Есть несохранённые изменения',
    saved: 'Сохранено',

    noDomainDetail:
      'Без домена доступен только предпросмотр (виден команде проекта).',
    noDomainFunnelPartDetail:
      'Без домена доступен только предпросмотр (виден команде проекта). ' +
      'Если этот квиз/пейволл — часть воронки с привязанным доменом, он уже ' +
      'доступен по адресу воронки.',
    domainUnknownDetail:
      'Не удалось спросить у сервера, привязан ли домен. Публикация не ' +
      'заблокирована: если домена действительно нет, сервер отклонит её сам.',
    domainUnknownBlocker: 'Не удалось проверить домены — повторите проверку',
    resourceReadonlyDetail:
      'Ресурс сверх лимита текущего тарифа и переведён в режим «только чтение»: ' +
      'смотреть и показывать в предпросмотре можно, сохранять и публиковать — нет. ' +
      'Вернитесь на прежний тариф, чтобы снова редактировать, — и обновите ' +
      'страницу конструктора: блокировка снимается при загрузке ресурса.',
    resourceReadonlyBlocker:
      'Ресурс заблокирован понижением тарифа — верните прежний тариф',
    projectDebtDetail: (credits: number) =>
      `Задолженность ${credits} кредитов — сохранение и публикация приостановлены до её погашения.`,
    projectDebtDetailNoAmount:
      'Долг перед сервисом — сохранение и публикация приостановлены до его погашения.',
    pricesStaleDetail: (stale: number) =>
      `Цены больше нет в подключённом аккаунте Stripe (${stale} ${
        stale === 1 ? 'блок' : 'блок(ов)'
      }) — выберите цены заново.`,
    pricesMissingDetail: (count: number) =>
      `Без цены ${count} ${count === 1 ? 'блок' : 'блок(ов)'}.`,
    pricesPostPurchaseSelectorDetail: (screen: string) =>
      `«Выбор тарифа» стоит только на экране после оплаты «${screen}» — до оплаты покупатель его не видит, и у кнопки оплаты нет цены. Перенесите «Выбор тарифа» на обычный экран или удалите его и задайте цену кнопке оплаты.`,
    blockerPayDebt: 'Погасите долг перед сервисом',
    blockerBindDomain: 'Привяжите домен и субдомен',
    blockerConnectPayments: 'Подключите платёжную систему в настройках проекта',
    blockerFillPrices: (count: number) =>
      `Заполните цены (${count} ${count === 1 ? 'блок' : 'блок(ов)'} без цены)`,
    blockerMovePlanSelectorOffPostPurchase: (screen: string) =>
      `Перенесите «Выбор тарифа» с экрана после оплаты «${screen}»`,
    blockerResolvePriceConflicts: (count: number) =>
      `Устраните конфликты цен (${count})`,
    blockerAddContentBlock: 'Добавьте хотя бы один блок содержимого',

    emptyTermsWarning:
      'Не заполнены ссылки на условия использования и политику конфиденциальности.',
    placeholderTermsWarning:
      'Ссылка ведёт на служебный адрес-заглушку (например example.com) — ' +
      'впишите адрес вашего настоящего документа.',

    paymentsNotConnectedHint:
      'Платёжная система не подключена — оплата не сработает. ' +
      'Подключите её в разделе «Настройки → Платежи».',

    // WEB-2057: тексты конфликтов цен собирает `pricingGate.ts` — модуль,
    // общий с рантаймом пейволла; они переехали в `authorHints.ts`.

    unnamedScreen: 'Без названия',
    confirmPublishTitleGeneric: 'Проверьте экраны перед публикацией',
    confirmPublishTitleEmptyOne: 'Экран без содержимого',
    confirmPublishTitleEmptyMany: 'Экраны без содержимого',
    confirmPublishTitleNoAdvanceOne: 'Экран без кнопки перехода',
    confirmPublishTitleNoAdvanceMany: 'Экраны без кнопки перехода',
    confirmPublishEmptyPart: (names: string, count: number) =>
      `${names}: ${
        count === 1 ? 'экран пуст' : 'экраны пусты'
      } — посетитель увидит только кнопку «Далее».`,
    confirmPublishNoAdvancePart: (names: string, _count: number) =>
      `${names}: нет кнопки перехода — посетитель увидит запасную «Далее».`,
    confirmPublishTitleOwnMoney: 'Платежи у вас свои',
    confirmPublishOwnMoneyPart: (count: number) =>
      `${
        count === 1
          ? 'В воронке есть блок оплаты'
          : `В воронке ${count} ${plural('ru', count, { one: 'блок', few: 'блока', many: 'блоков' })} оплаты`
      }, а платежи через нас отключены — ${
        count === 1 ? 'кнопка ничего не спишет' : 'кнопки ничего не спишут'
      }.`,
    confirmPublishAnyway: 'Опубликовать',
    confirmPublishBackToEditing: 'Назад',
    confirmPublishQuestion: (body: string) => `${body} Опубликовать?`,

    versionHistory: 'История версий',
    versionsCreatedOnPublish: 'Версии создаются при каждой публикации.',
    noSavedVersions: 'Нет сохранённых версий',
    versionLabel: (number: number, isFirst: boolean) =>
      `v${number} · ${isFirst ? 'первая публикация' : 'публикация'}`,
    // WEB-2131 (шаг 9): проверки и публикации — раздельно.
    versionsKindSwitchLabel: 'Какие версии показать',
    versionsKindPublish: 'Публикации',
    versionsKindTest: 'Проверки',
    testVersionsHint:
      'Проверка записывается каждым нажатием «На проверку». Откатиться к ' +
      'проверке нельзя — откат работает только по публикациям.',
    noTestVersions: 'Проверок ещё нет',
    testVersionLabel: (number: number) => `Проверка №${number}`,
    versionBodyPurged: 'Содержимое очищено — посмотреть нельзя',
    rollback: 'Откатить',
    rollbackConfirmTitle: (label: string) => `Откатить к ${label}?`,
    rollbackDraftWarning:
      'Текущий черновик будет заменён содержимым выбранной версии. Правки после ' +
      'неё будут потеряны.',
    rollbackPublishWarning:
      'Выбранная версия будет сразу опубликована — страница снова начнёт ' +
      'принимать трафик.',
    rollbackNoAppearanceWarning:
      'У этой версии не сохранено оформление (тема, палитра, шрифты) — ' +
      'после отката останется текущее оформление страницы.',
    // WEB-2142: история грузится страницами по 20 — кнопка просит следующую.
    versionsLoadMore: 'Показать ещё',
    versionsLoadFailed: 'Не удалось загрузить историю версий',
    versionsLoadMoreFailed:
      'Не удалось загрузить следующие версии. Попробуйте ещё раз.',
    versionRestored: 'Версия восстановлена',
    // WEB-2550: откат пускает право менять воронки (FUNNELS_WRITE), не только владельца.
    rollbackForbidden:
      'Нет права менять воронки этого проекта — попросите владельца проекта',
    rollbackNotOwner:
      'Откатить можно только то, что вы создали сами. Попросите владельца или администратора проекта.',
    rollbackFailed: 'Не удалось выполнить откат',
    // WEB-2548: «Посмотреть» — просмотр версии до отката.
    versionPreview: 'Посмотреть',
    versionPreviewBack: 'К списку версий',
    versionPreviewReadOnlyHint: 'Просмотр версии ничего не меняет в черновике.',
    versionPreviewLoadFailed: 'Не удалось загрузить версию',
    versionPreviewNoAppearance:
      'У этой версии не сохранено оформление, поэтому она показана без темы и ' +
      'палитры. После отката останется текущее оформление страницы.',
    versionPreviewEmpty: 'В этой версии нет экранов',
    versionPreviewPurged:
      'Содержимое этой версии очищено, посмотреть его нельзя',
    versionPreviewScreenOf: (index: number, count: number) =>
      `Экран ${index} из ${count}`,
    versionPreviewPrevScreen: 'Предыдущий экран',
    versionPreviewNextScreen: 'Следующий экран',
    versionPreviewRenderFailed:
      'Не удалось показать версию: сохранённое содержимое повреждено. К списку версий можно вернуться.',

    partQuiz: 'Квиз',
    partPaywall: 'Пейволл',
    partMap: 'Карта',
    addPartInline: (part: string) => `+ ${part}`,
    funnelPartAddFailed:
      'Не удалось добавить часть воронки. Попробуйте ещё раз.',
    funnelPartCreateFailed:
      'Не удалось создать часть воронки. Попробуйте ещё раз.',
    funnelPartLinkFailed: (part: 'quiz' | 'paywall') =>
      `Не удалось привязать часть воронки: создан непривязанный ${
        part === 'quiz' ? 'квиз' : 'пейволл'
      } — удалите вручную.`,

    closeModeMandatory: 'Обязательный — закрыть нельзя',
    closeModeClosable: 'Закрываемый — кнопка сразу',
    closeModeDelayed: 'Закрываемый с задержкой',
    closeModeShortMandatory: 'Показ: обязательный',
    closeModeShortClosable: 'Показ: закрываемый',
    closeModeShortDelayed: 'Показ: с задержкой',
    closeModeDrawerTitle: 'Показ пейволла',
    closeModeDrawerHint:
      'Как пейволл ведёт себя внутри приложения: может ли покупатель закрыть ' +
      'его, не оплатив. В обычном браузере ничего не меняется — кнопка ' +
      'закрытия там не показывается.',
    closeModeMandatoryHint:
      'Покупатель не сможет уйти с пейволла, не оплатив. Так ведут себя все пейволлы сегодня.',
    closeModeClosableHint: 'Кнопка закрытия видна с первой секунды.',
    closeModeDelayedHint:
      'Кнопка закрытия появится, когда пройдёт заданное время. Пока её нет, пейволл работает как обязательный.',
    closeModeDelaySeconds: 'Через сколько секунд показать кнопку',

    themeGalleryTitle: 'Тема оформления',
    themeNone: 'Без темы',
    themeNoneHint:
      'Снять выбранную тему — вернуться к настройкам по умолчанию.',
    themeFilterAll: 'Все',
    themeFilterDark: 'Тёмные',
    themeFilterLight: 'Светлые',
    themeFilterBright: 'Яркие',
    themeSampleHeading: 'Заголовок',
    themeSampleText: 'Пример текста темы',
    themeSampleButton: 'Кнопка',

    stylePackGalleryTitle: 'Стиль оформления',
    stylePackNone: 'Без оформления',
    stylePackOriginal: 'Оригинал',
    stylePackOriginalHint: 'Размеры и форма как на сайте',
    stylePackNoneHint:
      'Снять выбранный стиль-пак — вернуться к настройкам по умолчанию.',
    stylePackGalleryHint:
      'Стиль задаёт форму воронки: скругления, плотность, шрифтовую шкалу и ' +
      'поведение кнопки. Цвет остаётся за палитрой — их можно сочетать.',
    stylePackSampleOption: 'Вариант ответа',
    stylePackSampleOptionSecond: 'Второй вариант',
    stylePackSampleNoButton: 'Без кнопки',

    stylePackSoftRoundedName: 'Мягкий скруглённый',
    stylePackSoftRoundedDescription:
      'Крупные скругления, тонкая брендовая рамка, кнопка-пилюля внизу экрана.',
    stylePackWarmCreamName: 'Тёплый кремовый',
    stylePackWarmCreamDescription:
      'Молочный фон, тёплый почти-чёрный текст, крупные заголовки, выбор кольцом.',
    stylePackCleanWhiteName: 'Чистый белый',
    stylePackCleanWhiteDescription:
      'Белый минимализм, шапка со счётчиком шагов, плавное появление экрана.',
    stylePackFlatSharpName: 'Плоский строгий',
    stylePackFlatSharpDescription:
      'Малые скругления и плоские насыщенные цвета — «инженерный» вид без украшений.',
    stylePackCinematicPhotoName: 'Киношное фото',
    stylePackCinematicPhotoDescription:
      'Фотография на весь экран, белый текст поверх, никакого интерфейса вокруг.',
    stylePackDarkMysticName: 'Тёмный мистический',
    stylePackDarkMysticDescription:
      'Глубокий цветной фон, антиква кремово-золотым, символьные иконки, выбор кольцом.',
    stylePackEditorialSerifName: 'Журнальный',
    stylePackEditorialSerifDescription:
      'Тёплый бежевый фон, заголовок антиквой обычного начертания, список без зазоров.',
    stylePackMonoGlamName: 'Чёрно-белый глянец',
    stylePackMonoGlamDescription:
      'Ахроматика на слегка окрашенном фоне, чёрные плашки, кнопки-пилюли.',
    stylePackBrightPopName: 'Яркий акцентный',
    stylePackBrightPopDescription:
      'Насыщенный цвет на белом, рамка бренда в два пикселя, плашки со стрелкой.',
    stylePackPastelSerifName: 'Пастельный с антиквой',
    stylePackPastelSerifDescription:
      'Пастельные секции, крупная цветная антиква, тёплая контрастная кнопка.',
    stylePackTechMintName: 'Технологичный мятный',
    stylePackTechMintDescription:
      'Тёмная карточка-акцент на светлой странице, мятная кнопка, двухцветный заголовок.',
    stylePackProductPremiumName: 'Премиум-товар',
    stylePackProductPremiumDescription:
      'Фото товара в тёплой сцене, огромная антиква, золотая кнопка, промо-полоса.',
    stylePackBlueGradientName: 'Синий градиент',
    stylePackBlueGradientDescription:
      'Белые экраны, крупные вопросы слева, мягкие серые карточки и синий градиент у выбранного ответа и кнопок',
    stylePackSurveyFormName: 'Анкета',
    stylePackSurveyFormDescription:
      'Анкета-опросник: высокие строки в тонкой рамке, вопрос мелко и слева, обычная кнопка внизу, тонкая полоса прогресса',
    stylePackOutlinePillsName: 'Пилюли в рамке',
    stylePackOutlinePillsDescription:
      'Вытянутые пилюли в тонкой рамке с текстом по центру, крупный жирный вопрос по центру, кнопка-пилюля, просторно',
    stylePackFramedCenterName: 'Рамка по центру',
    stylePackFramedCenterDescription:
      'Варианты в тонкой рамке со скруглением 8, жирный вопрос по центру, полоса прогресса, кнопка прибита к низу',
    stylePackLeftSoftName: 'Слева, мягко',
    stylePackLeftSoftDescription:
      'Вопрос слева, варианты без рамки со скруглением 16, кнопка-пилюля прибита к низу',
    stylePackPlainListName: 'Простой список',
    stylePackPlainListDescription:
      'Невысокие варианты без рамки, вопрос по центру, тонкая полоса прогресса, кнопка сразу под вариантами',
    stylePackStepFormName: 'Пошаговая анкета',
    stylePackStepFormDescription:
      'Анкета по шагам: варианты в рамке со скруглением 12, вопрос по центру, прогресс отрезками, кнопка внизу',

    backToList: 'Назад к списку',
    backToFunnels: 'К списку воронок',
    backToQuizzes: 'К списку квизов',
    backToPaywalls: 'К списку пейволлов',
    untitledFunnel: 'Без названия',
    previewButton: 'Предпросмотр',
    undoTooltip: 'Отменить (Ctrl/Cmd+Z)',
    redoTooltip: 'Повторить (Ctrl/Cmd+Shift+Z)',
    domainCheckFailed: 'Не удалось проверить домены',
    domainBound: 'Домен привязан',
    bindDomainAction: 'Привязать домен',
    domainButton: 'Домен',
    domainModalTitle: (name: string) => `Домен для «${name}»`,
    domainModalFunnelFallback: 'воронки',
    fillPricesTooltip: 'Заполните цены — клик перейдёт к блоку',
    withoutPriceCount: (count: number) => `Без цены: ${count}`,
    previewUnpricedNotice: 'Цена не привязана — воронку не опубликовать.',
    previewUnpricedStaleNotice:
      'Цены больше нет в подключённом аккаунте Stripe — воронку не опубликовать.',
    previewUnpricedAction: 'Привязать цену',
    priceConflictsCount: (count: number) => `Конфликт цен: ${count}`,
    publish: 'Опубликовать',
    publishTooltip: 'Выпустить в бой — увидит весь рекламный трафик',
    jumpToBlock: 'Перейти к блоку',
    retryAction: 'Повторить',

    quizSaved: 'Квиз сохранён',
    paywallSaved: 'Пейволл сохранён',
    quizPublished: 'Квиз опубликован',
    paywallPublished: 'Пейволл опубликован',
    notEnoughCreditsSave:
      'Недостаточно средств на балансе. Пополните, чтобы сохранить.',
    notEnoughCreditsPublish:
      'Недостаточно средств на балансе. Пополните, чтобы опубликовать.',
    pageChangedElsewhere: 'Страницу изменили в другом окне — обновите редактор',
    saveFailed: 'Не удалось сохранить',
    saveRejected: 'Не удалось сохранить: сервер отклонил изменения',
    publishFailed: 'Не удалось опубликовать',
    sendToReview: 'Проверить',
    sendToReviewTooltip:
      'Зафиксировать текущее состояние — увидят только те, кому вы дали ссылку',
    reviewBlockedAiTurn: 'Идёт ход ИИ-редактора — дождитесь окончания',
    reviewBlockedTranslation:
      'Режим перевода: к тестовой версии перевод не догоняется',
    reviewBlockedSaving: 'Идёт сохранение черновика — подождите',
    reviewBlockedTurnDecision:
      'Сначала ответьте в окне «Ход мог сохраниться на сервере»',
    reviewSent: 'Отправлено на проверку — версия зафиксирована',
    reviewFailed: 'Не удалось отправить на проверку',
    reviewNotOwner:
      'Участник может отправить на проверку только то, что создал сам. Попросите владельца или администратора проекта.',
    reviewBlockedReadonly:
      'Ресурс заблокирован понижением тарифа — верните прежний тариф, чтобы отправлять на проверку',
    reviewBlockedDebt:
      'У организации долг по кредитам — пока он не погашен, отправить на проверку нельзя',
    reviewStateLabel: 'Проверка:',
    reviewStateNever: 'ещё не отправляли',
    reviewStateChanged: 'есть изменения с последней проверки',
    reviewStateLiveBehind: (date: string) =>
      `боевая отстаёт (опубликовано ${date})`,
    reviewStateLiveOk: 'боевая совпадает',
    reviewStateNotPublished: 'ещё не опубликовано',
    // WEB-2595: все живые воронки с этим куском на паузе. Не «посетители не видят»: кусок
    // может отдаваться мимо воронки (прямой маршрут домена, умная ссылка, предпросмотр).
    reviewStateFunnelsPaused: 'все воронки с ним на паузе',
    publishReviewChangedTitle: 'Публикуется не то, что смотрели коллеги',
    publishReviewChangedBody: (date: string) =>
      `Есть изменения с последней проверки (${date}). Это разрешено — просто знайте, что уедет свежее рабочее состояние.`,
    fillPricesBeforePublish: (count: number) =>
      `Укажите цены перед публикацией: без цены ${count} ${plural('ru', count, {
        one: 'блок',
        few: 'блока',
        many: 'блоков',
      })}`,
    stalePricesBeforePublish: (staleCount: number) =>
      `Цены больше нет в подключённом аккаунте Stripe (${staleCount} ${plural(
        'ru',
        staleCount,
        {
          one: 'блок',
          few: 'блока',
          many: 'блоков',
        },
      )}) — выберите цены заново перед публикацией`,
    draftRevertedOneStep: 'Черновик возвращён к предыдущему шагу',
    revertTurnFailed: 'Не удалось отменить ход',
    revertTurnWaitPrevious:
      'Ещё идёт прошлая отмена хода — подождите, пока она закончится',
    manualEditsReplaced:
      'Правки, сделанные вручную во время хода ИИ, заменены его результатом.',
    draftChangedDuringTurn:
      'Черновик менялся, пока шёл ход, — показываем свежую версию; итог хода в истории чата.',
    debtBanner: (credits: number) =>
      `Задолженность ${credits} кредитов — сохранение и публикация приостановлены.`,
    topUp: 'Пополнить',
    topUpAskOwner:
      'Обратитесь к владельцу организации, чтобы пополнить баланс.',
    screenNumberLabel: (index: number, name: string) =>
      `экран ${index}${name ? ` «${name}»` : ''}`,
    rightTabProperties: 'Свойства',
    rightTabAi: 'ИИ',
    aiEditsCount: (count: number) =>
      `· ${count} ${plural('ru', count, { one: 'правка', few: 'правки', many: 'правок' })}`,
    clearBlockSelectionAria: 'Снять выбор блока',
    pickBlockInLayers: 'Выберите блок в списке слоёв или на превью',
    themeCleared: 'Тема снята — применены настройки по умолчанию',
    themeApplied: (name: string) => `Тема «${name}» применена`,
    themeReplaceColorsTitle: 'Заменить цвета воронки?',
    themeReplaceColorsBody:
      'Тема заменит цвета кнопок, фон и цвет текста, которые сейчас стоят в воронке. Вернуть их можно кнопкой «Отменить», пока страница не перезагружена.',
    themeReplaceColorsBodyClear:
      'Снятие темы уберёт и цвета кнопок, фон и цвет текста, которые сейчас стоят в воронке. Вернуть их можно кнопкой «Отменить», пока страница не перезагружена.',
    themeReplaceColorsApply: 'Применить тему',
    themeReplaceColorsClear: 'Снять тему',
    themeReplaceColorsKeep: 'Оставить свои цвета',
    stylePackCleared: 'Оформление снято — применены настройки по умолчанию',
    stylePackTakeColors: 'Взять цвета стиля',
    stylePackColorsTaken: 'Цвета стиля применены',
    stylePackApplied: (name: string) => `Стиль «${name}» применён`,
    stylePackOriginalApplied: 'Стиль: Оригинал — размеры как на сайте',
    stylePackAppliedWithRisk: (name: string, risk: string) =>
      `Стиль «${name}» применён. ${risk}`,
    stylePackColorsTakenWithRisk: (risk: string) =>
      `Цвета стиля применены. ${risk}`,
    stylePackTakeColorsBody:
      'Цвета стиля заменят цвета кнопок, фон и цвет текста, которые сейчас стоят в воронке. Вернуть их можно кнопкой «Отменить», пока страница не перезагружена.',
    contrastRiskAfterTheme: (count: number) =>
      `В ${count} ${
        count === 1 ? 'блоке со своим фоном' : 'блоках со своим фоном'
      } текст может стать плохо читаемым — проверьте вручную.`,
    themeClearedWithRisk: (risk: string) => `Тема снята. ${risk}`,
    themeAppliedWithRisk: (name: string, risk: string) =>
      `Тема «${name}» применена. ${risk}`,

    addLanguage: 'Добавить язык',
    translating: 'Перевод...',
    translate: 'Перевести',
    sourceLanguagePlaceholder: 'Выберите исходный язык',
    deleteTranslation: 'Удалить перевод',
    saveBeforeTranslate: 'Сохраните изменения перед переводом.',
    saveBeforeSourceChange: 'Сохраните изменения перед сменой исходного языка.',
    changeSourceLanguageTitle: 'Изменить исходный язык',
    changeLanguageAction: 'Изменить язык',
    sourceLanguageChanged: 'Исходный язык изменён',
    sourceLanguageChangeFailed: 'Не удалось сменить исходный язык',
    translationDeleteFailed: 'Не удалось удалить перевод',
    topUpBalance: 'Пополнить баланс',
    sourceChangeTextsStay: (part: 'quiz' | 'paywall') =>
      `Тексты ${part === 'quiz' ? 'воронки' : 'пейволла'} останутся как есть`,
    sourceChangePlayerStrings:
      'Служебные строки плеера — «Далее», «в месяц» — сменятся сразу',
    sourceChangeReorderTranslations: 'Готовые переводы закажите заново',
    sourceChangeBlockedByTranslation: (part: 'quiz' | 'paywall') =>
      `На этот язык уже заказан перевод ${
        part === 'quiz' ? 'воронки' : 'пейволла'
      }. Сделать его исходным нельзя: оригинал и перевод оказались бы на одном ` +
      'языке, и оплаченный перевод превратился бы в мёртвый груз — посетителю ' +
      'он больше не будет показываться, а догон продолжит его вести и списывать ' +
      'за это кредиты. Удалите перевод в этом же меню и повторите — либо ' +
      'выберите другой язык.',
    translationEditTitle: (language: string) => `Правка перевода — ${language}`,
    translationSaveWithCount: (count: number) => `Сохранить (${count})`,
    translationEditFree:
      'Правка бесплатная — повторный перевод стоил бы кредитов. Меняется только ' +
      'текст, настройки блоков не затрагиваются.',
    translationChangedWhileOpen:
      'Перевод изменился, пока окно правки было открыто: система догнала недостающие строки.',
    translationSaved: 'Перевод сохранён',
    translationSaveFailed: 'Не удалось сохранить перевод',
    translationMissingCount: (count: number) =>
      `Система считает недобранными строк: ${count}. Они помечены ниже — система ` +
      'допереводит их сама, править их руками необязательно.',
    translationUnmatchedCount: (count: number) =>
      `Из них ${count} в этом окне не показываются (служебные поля или уже удалённые блоки).`,
    translationOnlyMissing: 'Только недобранные',
    translationNothingToEdit: 'В переводе нет текста для правки',
    translationMissingBadge: 'не добрано',

    priceIntervalMonth: 'в месяц',
    priceIntervalYear: 'в год',
    priceIntervalWeek: 'в неделю',
    priceIntervalDay: 'в день',
    priceIntervalOneTime: 'разовый',
    createPriceTitle: 'Создать цену Stripe',
    createPriceAction: 'Создать',
    createPriceFailed: 'Не удалось создать цену. Попробуйте ещё раз.',
    productNameLabel: 'Название продукта',
    productNamePlaceholder: 'например, Премиум план',
    amountLabel: 'Сумма',
    currencyLabel: 'Валюта',
    intervalLabel: 'Периодичность',
    // WEB-2298 п.5: поле работает только для списаний через наше API.
    accessDaysLabel: 'Срок доступа, дней (только для списаний через API, необязательно)',
    accessDaysPlaceholder: 'Пусто — доступ по этой цене сервером не выдаётся',
    trialDaysLabel: 'Trial, дней (необязательно)',
    trialDaysPlaceholder: 'Без пробного периода',
    pricePlaceholder: 'Выберите цену Stripe',
    priceGoneNotice: (withRefresh: boolean) =>
      'Выбранной цены больше нет среди доступных: её удалили в Stripe или она ' +
      'осталась от прежних ключей. Оплата по ней не пройдёт — выберите цену ' +
      `заново${withRefresh ? ' (кнопка ↻ обновит список)' : ''}.`,
    newPriceButton: '+ Новая цена',
    stripeNotConnected: 'Stripe не подключён.',
    connectStripe: 'Подключить Stripe',
    stripeConnectTail: 'чтобы добавить цены, а затем импортировать их сюда.',
    noStripePricesYet: 'Пока нет цен Stripe.',
    importFromStripe: 'Импортировать из Stripe',
    // WEB-2438 Ж2а: вкладки «Stripe» нет — «Платежи»; импорт — в «Подписки → Товары».
    stripeImportHintPrefix: 'Подключите Stripe в',
    stripeImportHintSettings: 'Настройки проекта → Платежи',
    stripeImportHintMiddle: 'и затем в «Подписки → Товары» нажмите',
    stripeImportHintImport: 'Импортировать из Stripe',
    stripeImportHintTail: '— тогда ваши цены появятся здесь.',
    refreshPricesTooltip: 'Перечитать список цен из Stripe',
    refreshPricesButton: '↻ Обновить из Stripe',
    clearLockedPrice: 'Очистить сохранённую цену',

    portalInvalidLinkPreviewHint:
      'Так экран выглядит, если ссылка недействительна: покупатель видит ' +
      'приглашение войти по почте. Печатает его верхний портальный блок экрана.',
    portalNothingToOfferHint:
      'Предлагать нечего (нет подходящей подписки или не выбрана цена со скидкой) ' +
      '— покупателю блок не показывается.',
    portalSubscriptionListPreviewHint: 'Здесь появятся подписки покупателя',

    destinationUrlTooLong: (max: number) =>
      `URL слишком длинный (макс ${max} символов)`,
    destinationUrlInvalid: 'Введите корректный URL',
    destinationUrlNotHttps: 'URL должен начинаться с https://',

    scratchCoverGoldFoil: 'Золотая фольга',
    scratchCoverSilverFoil: 'Серебро',
    scratchCoverKraft: 'Крафт-бумага',
    scratchCoverDark: 'Тёмный градиент',

    optionLimitRejected: (
      blockType: string,
      field: string,
      count: number,
      max: number,
    ) =>
      `Правка блока "${blockType}" отклонена: поле "${field}" — ` +
      `${count} элементов при потолке ${max}. ` +
      'Список оставлен прежним: молча обрезать его нельзя (в элементах ' +
      'авторский текст и переходы ветвления).',

    blockNoticePromoTicket: 'Оформление: код не проверяется и скидку не даёт.',
    blockNoticeRestorePurchases:
      'Только в приложении с SDK: в обычном браузере скрыта.',
    blockNoticeCloseWebview:
      'Для приложения с SDK: в обычном браузере видна, только если задана своя ссылка.',

    shareButton: 'Поделиться',
    shareButtonTooltip: 'Ссылка на просмотр и выданные ссылки',
    shareNoEditRight: 'Доступно тем, кто может редактировать воронку',
    shareModalTitleQuiz: 'Поделиться предпросмотром квиза',
    shareModalTitlePaywall: 'Поделиться предпросмотром пейволла',
    shareModalTitleFunnel: 'Поделиться предпросмотром воронки',
    shareNote:
      'Получатель видит версию «На проверку». Проход не в отчётах, оплатить нельзя.',
    shareNoteHint:
      'Адрес ссылки не меняется, когда вы отправляете новые версии «На проверку», — рассылать заново не нужно. Код доступа и поведение версий настраиваются там же, в шапке редактора, под стрелкой у кнопки «На проверку».',
    shareNoteHintAria: 'Подробнее о ссылке',
    shareTtlLabel: (max: number) => `Срок, часов (1–${max})`,
    shareTtlPreset: (hours: number) => `${hours} ч`,
    shareTtlTooLong: (max: number) => `Максимум ${max} часов — укажите меньше.`,
    shareTtlMissing: (max: number) => `Укажите от 1 до ${max} часов.`,
    shareCreate: 'Создать',
    shareCodeGenerate: 'Сгенерировать код',
    shareCodeRegenerate: 'Сгенерировать другой код',
    shareCodeRemove: 'Убрать код',
    shareCodeSharedHint: 'Код один на все ссылки этой воронки',
    shareCodeSharedHintQuiz: 'Код один на все ссылки этого квиза',
    shareCodeSharedHintPaywall: 'Код один на все ссылки этого пейволла',
    shareCreatedLabel: 'Ссылка',
    shareUrlUnavailable: 'Адрес ссылки недоступен — обратитесь в поддержку',
    shareValidUntil: (date: string) => `Действует до ${date}`,
    shareCreatedCode: 'Код доступа:',
    shareCodeSendSeparately:
      'Отправьте код отдельным сообщением, не вместе со ссылкой',
    shareCopy: 'Скопировать',
    shareCopyLinkTooltip: 'Скопировать ссылку',
    shareLinkCopied: 'Ссылка скопирована',
    shareCodeCopied: 'Код скопирован',
    shareCopyFailed:
      'Не удалось скопировать — выделите текст и скопируйте вручную',
    // WEB-2800: копирование из строки таблицы ссылок не удалось — адрес открыт под строкой.
    shareCopyFailedRowShown: 'Не удалось скопировать — адрес ссылки открыт под её строкой: выделите его и скопируйте вручную',
    shareManualCopyHint: 'Выделите адрес и скопируйте вручную:',
    shareLinksTitleQuiz: 'Ссылки на этот квиз',
    shareLinksTitlePaywall: 'Ссылки на этот пейволл',
    shareLinksTitleFunnel: 'Ссылки на эту воронку',
    shareLinksEmpty: 'Ссылок пока нет',
    shareLinksLoadFailed: 'Не удалось загрузить ссылки',
    shareRetry: 'Повторить',
    shareColCreated: 'Создана',
    shareColExpires: 'Истекает',
    shareColVersions: 'Версии',
    shareColCode: 'Код',
    shareColOpens: 'Заходов',
    shareExpiredMark: 'истекла',
    shareCodeYes: 'есть',
    shareCodeNo: 'без кода',
    // WEB-2788: код включён, а сохранённый не расшифровался (сменили ключ, испорчена запись).
    shareCodeBroken: 'код нужно создать заново',
    shareCodeBrokenHint: 'Код повреждён, получатель не сможет войти',
    shareCodeGenerateNew: 'Сгенерировать новый код',
    shareRevoke: 'Отозвать',
    shareRevokeConfirm:
      'Отозвать ссылку? Она сразу перестанет открываться у всех.',
    shareCancel: 'Отмена',
    shareRevoked: 'Отозвана',
    shareRevokedToast:
      'Ссылка отозвана — она сразу перестала открываться у всех, кому её отправили',
    sharePlanStopped: 'остановлена тарифом',
    // WEB-2789: квиз или экран оплаты воронки удалён — получатель видит отказ.
    shareFunnelBrokenStopped:
      'остановлена: воронка сломана (её квиз или пейволл удалён)',
    // WEB-3364: причина остановки, которой кабинет не знает, — общая подпись.
    shareOtherStopped: 'остановлена — ссылка не открывается',
    shareCreateFailed: 'Не удалось создать ссылку',
    shareRevokeFailed: 'Не удалось отозвать ссылку',
    // WEB-3749, юнит «предпросмотр»: окно «Предпросмотр» таблиц (эталон content-dlg.jsx SH).
    sharePvTitle: (name: string) => `Предпросмотр — ${name}`,
    shareSelf: 'Посмотреть самому',
    shareSelfHint: 'Текущий черновик — в новой вкладке',
    shareOpenSelf: 'Открыть',
    shareOther: 'Ссылка для другого человека',
    shareDone: 'Готово',
    shareRevokeYes: 'Да, отозвать',
    sharePagePrev: 'Назад',
    sharePageNext: 'Дальше',
    reviewSettingsArrowTooltip:
      'Настройки проверки: ссылка, код доступа, версии',
    reviewLinkTitle: 'Ссылка на просмотр',
    reviewLinkHint:
      'Отправьте её тому, кто должен посмотреть. Адрес не меняется при новых отправках на проверку.',
    reviewLinkCreate: (hours: number) => `Создать ссылку на ${hours} ч`,
    reviewSettingsTitle: 'Настройки проверки',
    reviewSettingsHint: 'Действуют для всех ссылок этой воронки.',
    reviewSettingsHintQuiz: 'Действуют для всех ссылок этого квиза.',
    reviewSettingsHintPaywall: 'Действуют для всех ссылок этого пейволла.',
    reviewAskCode: 'Спрашивать код доступа',
    reviewGenCode: 'Придумать код за вас: без нуля и буквы O, без единицы и I',
    reviewCodeBroken:
      'Сохранённый код повреждён (сменили ключ шифрования или испорчена запись): получатель увидит поле кода, а войти не сможет никто. Сгенерируйте новый код.',
    reviewCopyCode: 'Скопировать код',
    reviewCodeInvalid: '6 знаков: буквы и цифры без 0, O, 1, I',
    reviewVersions: 'Версии',
    reviewFirstTimeNote:
      'Это окно открылось само, потому что вы отправляете на проверку впервые. Дальше — только по стрелке.',
    reviewSettingsLoadFailed: 'Не удалось загрузить настройки проверки',
    reviewSettingsSaveFailed: 'Не удалось сохранить настройки проверки',
    reviewCodeGenerateFailed: 'Не удалось сгенерировать код',
    // WEB-2560: отказ сохранения при уходе из редактора — в корневом держателе.
    leaveSaveLostStale: (name: string) =>
      `Последние правки${name.trim() ? ` в «${name.trim()}»` : ''} не сохранены: черновик изменили в другом окне или другой участник. Откройте его снова и проверьте.`,
    leaveSaveLost: (name: string) =>
      `Последние правки${name.trim() ? ` в «${name.trim()}»` : ''} сохранить не удалось. Откройте его снова и проверьте.`,
  },
);
